import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Card, Row, Col, Select, DatePicker,
    Table, Tag, Spin, Button, Progress, Alert, Empty, Divider, Tooltip
} from 'antd';
import {
    ReloadOutlined, TrophyOutlined, CalendarOutlined, StarFilled,
    RiseOutlined, FallOutlined, MinusOutlined, ApartmentOutlined,
    BarChartOutlined, PieChartOutlined, LineChartOutlined,
    TableOutlined, FilterOutlined, WarningFilled, CheckCircleFilled,
    FilePdfOutlined, FileImageOutlined, LoadingOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import QoCService from '../../services/QoCService';
import { FacilityReportModal } from './QoCFacilityReport';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';
import { trailPageView, trailReportGenerate, trailReportExport, TRAIL_MODULES } from '../../utils/activityTrail';
const { RangePicker } = DatePicker;
const { Option, OptGroup } = Select;

const CLAUSES = [
    { code:'AACC', title:'Access, Assessment and Continuity of Care', color:'#0ea5e9' },
    { code:'COP',  title:'Care of the Patient',                        color:'#8b5cf6' },
    { code:'MOM',  title:'Management of Medication',                   color:'#f59e0b' },
    { code:'PRPE', title:"Patient Rights and Patient Education",        color:'#10b981' },
    { code:'IPCM', title:'Infection Prevention and Control Mgmt',      color:'#ef4444' },
    { code:'PSQI', title:'Patient Safety and Quality Improvement',     color:'#3b82f6' },
    { code:'GROM', title:'Governance, Roles and Responsibilities',     color:'#6366f1' },
    { code:'FMS',  title:'Facility Management and Safety',             color:'#ec4899' },
    { code:'HRE',  title:'Human Resource and Education',               color:'#14b8a6' },
    { code:'ISM',  title:'Information System Management',              color:'#a855f7' },
];
const LEVELS = ['Clinic without Maternity','Clinic with Maternity','Public Health Unit','Regional Hospital','National Referral Hospital'];
const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#389e0d' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#1890ff' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#52c41a' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#faad14' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#fa8c16' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#f5222d' },
];
const SCHED_STATUS = {
    scheduled:   { color:'#1890ff', label:'Scheduled',   bg:'#e6f4ff' },
    in_progress: { color:'#faad14', label:'In Progress', bg:'#fffbe6' },
    completed:   { color:'#52c41a', label:'Completed',   bg:'#f6ffed' },
    overdue:     { color:'#f5222d', label:'Overdue',     bg:'#fff2f0' },
    cancelled:   { color:'#bbb',    label:'Cancelled',   bg:'#f5f5f5' },
};
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i=>(
        <StarFilled key={i} style={{color: i<=count?'#faad14':'#e8e8e8', fontSize:size}}/>
    ))}</span>;
}

function KpiCard({ label, value, suffix='', color='#1890ff', icon, sub }) {
    return (
        <div style={{background:`${color}09`,border:`1px solid ${color}28`,borderRadius:10,
            padding:'14px 18px',height:'100%',position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:10,top:10,opacity:0.1,fontSize:40}}>{icon}</div>
            <div style={{fontSize:10,color:'#888',fontWeight:700,textTransform:'uppercase',
                letterSpacing:0.8,marginBottom:4}}>{label}</div>
            <div style={{fontSize:26,fontWeight:900,color,lineHeight:1}}>
                {value}<span style={{fontSize:13,marginLeft:3}}>{suffix}</span>
            </div>
            {sub && <div style={{marginTop:4,fontSize:10,color:'#888'}}>{sub}</div>}
        </div>
    );
}

function SectionTitle({ id, icon, title, subtitle, color='#1890ff' }) {
    return (
        <div id={id} style={{display:'flex',alignItems:'center',gap:10,
            margin:'32px 0 16px',padding:'12px 16px',
            background:`linear-gradient(135deg,${color}12,${color}05)`,
            borderLeft:`5px solid ${color}`,borderRadius:'0 10px 10px 0'}}>
            <span style={{fontSize:22,color}}>{icon}</span>
            <div>
                <div style={{fontWeight:900,fontSize:16,color:'#1a1a2e'}}>{title}</div>
                {subtitle && <div style={{fontSize:11,color:'#888',marginTop:1}}>{subtitle}</div>}
            </div>
        </div>
    );
}

// ─── Per-card export hook ─────────────────────────────────────────────────────
function useNodeExport(nodeRef) {
    const [busy, setBusy] = useState(null);

    const capture = async (el, scale) => {
        const { default: h2c } = await import('html2canvas');
        const hidden = [];
        el.querySelectorAll('[data-xcard-btns]').forEach(n => {
            n._v = n.style.visibility; n.style.visibility = 'hidden'; hidden.push(n);
        });
        // Allow overflow so ECharts aren't clipped
        const origOverflow = el.style.overflow;
        el.style.overflow = 'visible';
        const rect   = el.getBoundingClientRect();
        const scrollW = Math.max(el.scrollWidth,  Math.ceil(rect.width));
        const scrollH = Math.max(el.scrollHeight, Math.ceil(rect.height));
        const canvas = await h2c(el, {
            scale, useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
            logging: false, width: scrollW, height: scrollH,
            windowWidth: scrollW, windowHeight: scrollH, scrollX: 0, scrollY: 0,
            // Copy ECharts canvas pixels into cloned document
            onclone: (clonedDoc) => {
                const origs   = el.querySelectorAll('canvas');
                const cloneds = clonedDoc.querySelectorAll('canvas');
                origs.forEach((orig, i) => {
                    const cl = cloneds[i];
                    if (cl && orig.width > 0 && orig.height > 0) {
                        try { cl.width=orig.width; cl.height=orig.height; cl.getContext('2d').drawImage(orig,0,0); }
                        catch(e) { /* skip cross-origin */ }
                    }
                });
            },
        });
        el.style.overflow = origOverflow;
        hidden.forEach(n => { n.style.visibility = n._v || ''; delete n._v; });
        return canvas;
    };

    const exportPNG = async (filename) => {
        if (!nodeRef?.current || busy) return;
        setBusy('png');
        try {
            const canvas = await capture(nodeRef.current, 2.5);
            const a = document.createElement('a');
            a.download = filename + '.png';
            a.href = canvas.toDataURL('image/png');
            a.click();
        } catch(e) { console.error('PNG export failed', e); }
        finally { setBusy(null); }
    };

    const exportPDF = async (filename, title) => {
        if (!nodeRef?.current || busy) return;
        setBusy('pdf');
        try {
            const canvas  = await capture(nodeRef.current, 2.0);
            const mod     = await import('jspdf');
            const jsPDF   = mod.jsPDF || mod.default?.jsPDF || mod.default;
            const MG = 8, PAGE_W = 297, PAGE_H = 210, HDR = 12;
            const usableW = PAGE_W - MG * 2;
            const scale   = usableW / canvas.width;
            const imgH    = canvas.height * scale;
            const availH  = PAGE_H - HDR - 4;
            const pdf     = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const now     = new Date().toLocaleDateString('en-GB',{ day:'2-digit', month:'short', year:'numeric' });
            const drawHeader = (label) => {
                pdf.setFillColor(15,23,42); pdf.rect(0,0,PAGE_W,HDR,'F');
                pdf.setTextColor(255,255,255); pdf.setFont('helvetica','bold'); pdf.setFontSize(8);
                pdf.text(label, MG, 8.5);
                pdf.setFont('helvetica','normal'); pdf.setFontSize(7);
                pdf.text('Eswatini QoC Platform  |  ' + now, PAGE_W-MG, 8.5, { align:'right' });
            };
            // PNG preserves exact chart colors — JPEG loses them
            const toImg = (c) => c.toDataURL('image/png');
            drawHeader(title || filename);
            if (imgH <= availH) {
                pdf.addImage(toImg(canvas), 'PNG', MG, HDR+2, usableW, imgH);
            } else {
                const slicePx = Math.floor(availH / scale);
                let startPx=0, pageNum=1;
                while (startPx < canvas.height) {
                    if (pageNum > 1) { pdf.addPage('a4','landscape'); drawHeader(title||filename); }
                    const endPx = Math.min(startPx+slicePx, canvas.height);
                    const sc = document.createElement('canvas');
                    sc.width=canvas.width; sc.height=endPx-startPx;
                    sc.getContext('2d').drawImage(canvas,0,startPx,canvas.width,sc.height,0,0,canvas.width,sc.height);
                    pdf.addImage(toImg(sc), 'PNG', MG, HDR+2, usableW, sc.height*scale);
                    startPx=endPx; pageNum++;
                }
            }
            pdf.save(filename + '.pdf');
        } catch(e) { console.error('PDF export failed', e); }
        finally { setBusy(null); }
    };

    return { busy, exportPNG, exportPDF };
}

// ─── XCard — Card with built-in per-card PNG + PDF export ────────────────────
function XCard({ title, filename, children, extra, style, bodyStyle, size='small' }) {
    const ref  = useRef(null);
    const safe = (filename || (typeof title === 'string' ? title : 'chart')).replace(/[^a-zA-Z0-9_-]/g, '_');
    const label = typeof title === 'string' ? title : safe;
    const { busy, exportPNG, exportPDF } = useNodeExport(ref);

    const btns = (
        <span data-xcard-btns style={{ display:'inline-flex', gap:4, marginLeft:8 }}>
            <Tooltip title="Save as PNG">
                <span onClick={() => exportPNG(safe)}
                    style={{ cursor: busy==='png' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #d9d9d9', color:'#555', background:'#fafafa',
                        opacity: busy && busy!=='png' ? 0.4 : 1 }}>
                    {busy==='png' ? <LoadingOutlined style={{fontSize:10}}/> : <FileImageOutlined style={{fontSize:10}}/>}
                    PNG
                </span>
            </Tooltip>
            <Tooltip title="Save as PDF">
                <span onClick={() => exportPDF(safe, label)}
                    style={{ cursor: busy==='pdf' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #ffccc7', color:'#cf1322', background:'#fff2f0',
                        opacity: busy && busy!=='pdf' ? 0.4 : 1 }}>
                    {busy==='pdf' ? <LoadingOutlined style={{fontSize:10,color:'#cf1322'}}/> : <FilePdfOutlined style={{fontSize:10}}/>}
                    PDF
                </span>
            </Tooltip>
        </span>
    );

    return (
        <div ref={ref}>
            <Card size={size} style={style} bodyStyle={bodyStyle}
                title={title}
                extra={<>{extra}{btns}</>}>
                {children}
            </Card>
        </div>
    );
}

// ─── Full-dashboard PDF export button ────────────────────────────────────────
function ExecDashPDF({ contentRef, filters = {} }) {
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress]   = useState(0);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true); setProgress(0);
        let jsPDFLib, html2canvasLib;
        try {
            const [jspdfMod, h2cMod] = await Promise.all([ import('jspdf'), import('html2canvas') ]);
            jsPDFLib       = jspdfMod.jsPDF || jspdfMod.default?.jsPDF || jspdfMod.default;
            html2canvasLib = h2cMod.default || h2cMod;
        } catch(e) { import('antd').then(m=>m.message.error('PDF library failed: '+e.message)); setExporting(false); return; }

        setProgress(10);
        const el = contentRef?.current;
        if (!el) { setExporting(false); return; }

        try {
            const toHide = [];
            ['[data-pdf-hide]','.page-sidebar-wrapper','[data-xcard-btns]'].forEach(sel => {
                el.querySelectorAll(sel).forEach(n => {
                    if (n.style.visibility !== 'hidden') { n._v = n.style.visibility; n.style.visibility = 'hidden'; toHide.push(n); }
                });
            });
            setProgress(20);
            const canvas = await html2canvasLib(el, {
                scale:1.6, useCORS:true, allowTaint:true, backgroundColor:'#f4f6fb',
                scrollY:-window.scrollY, scrollX:0,
                windowWidth:el.scrollWidth, windowHeight:el.scrollHeight, logging:false,
            });
            toHide.forEach(n => { n.style.visibility = n._v || ''; delete n._v; });
            setProgress(75);

            const period = filters.period_label || 'All Periods';
            const generatedAt = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
            const PAGE_W=297, PAGE_H=210, MG=10, HEADER_H=13, FOOTER_H=9;
            const usableW = PAGE_W - MG*2;
            const usableH = PAGE_H - HEADER_H - FOOTER_H - 2;
            const scale   = usableW / canvas.width;
            const pageHeightPx = usableH / scale;

            const SECTION_IDS = ['sec-overview','sec-trend','sec-schedules','sec-clauses','sec-substandards','sec-mes','sec-kpis','sec-qindex','sec-surveys'];
            const elRect = el.getBoundingClientRect();
            const sectionBreaks = SECTION_IDS.map(id => {
                const sec = el.querySelector(`#${id}`); if (!sec) return null;
                const r = sec.getBoundingClientRect();
                return (r.top - elRect.top + (el.scrollTop||0)) * 1.6;
            }).filter(Boolean).sort((a,b)=>a-b);

            const pages = []; let cursor = 0;
            while (cursor < canvas.height) {
                const idealEnd = cursor + pageHeightPx;
                if (idealEnd >= canvas.height) { pages.push({startPx:cursor,endPx:canvas.height}); break; }
                const leeway = pageHeightPx * 0.15;
                const best = sectionBreaks.filter(b=>b>cursor+pageHeightPx*0.5&&b<=idealEnd+leeway).sort((a,b)=>Math.abs(a-idealEnd)-Math.abs(b-idealEnd))[0];
                const endPx = best || idealEnd;
                pages.push({startPx:cursor,endPx}); cursor=endPx;
            }

            const totalPages = pages.length;
            const pdf = new jsPDFLib({orientation:'landscape',unit:'mm',format:'a4'});
            const drawChrome = (pn) => {
                pdf.setFillColor(15,23,42); pdf.rect(0,0,PAGE_W,HEADER_H,'F');
                pdf.setTextColor(255,255,255); pdf.setFontSize(8.5); pdf.setFont('helvetica','bold');
                pdf.text('ESWATINI MINISTRY OF HEALTH — National Quality Management Programme', MG, 8.5);
                pdf.setFont('helvetica','normal'); pdf.setFontSize(7.5);
                pdf.text(`Executive Dashboard  |  Period: ${period}  |  ${generatedAt}  |  Page ${pn} of ${totalPages}`, PAGE_W-MG, 8.5, {align:'right'});
                pdf.setFillColor(241,245,249); pdf.rect(0,PAGE_H-FOOTER_H,PAGE_W,FOOTER_H,'F');
                pdf.setTextColor(100,116,139); pdf.setFontSize(7);
                pdf.text('Confidential — Eswatini Ministry of Health QoC Platform', MG, PAGE_H-3);
                pdf.text(`${pn} / ${totalPages}`, PAGE_W-MG, PAGE_H-3, {align:'right'});
            };
            for (let i=0; i<pages.length; i++) {
                if (i>0) pdf.addPage('a4','landscape');
                drawChrome(i+1);
                const {startPx,endPx} = pages[i]; const sliceH=endPx-startPx;
                const sc=document.createElement('canvas'); sc.width=canvas.width; sc.height=sliceH;
                sc.getContext('2d').drawImage(canvas,0,startPx,canvas.width,sliceH,0,0,canvas.width,sliceH);
                pdf.addImage(sc.toDataURL('image/jpeg',0.88),'JPEG',MG,HEADER_H+1,usableW,sliceH*scale);
                setProgress(75+Math.round(((i+1)/pages.length)*22));
            }
            setProgress(97);
            pdf.save(`QoC_Executive_Dashboard_${period.replace(/[^A-Za-z0-9]/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
            setProgress(100);
            import('antd').then(m=>m.message.success(`PDF exported — ${totalPages} pages`));
            trailReportExport(TRAIL_MODULES.EXECUTIVE_DASH, 'Executive Dashboard', 'PDF', { period: filters.period_label || 'All', pages: totalPages });
        } catch(err) { import('antd').then(m=>m.message.error('Export failed: '+err.message)); console.error(err); }
        finally { setTimeout(()=>{setExporting(false);setProgress(0);},1500); }
    };

    return (
        <div style={{display:'inline-flex',alignItems:'center',gap:8}} data-pdf-hide="true">
            <Button type="primary" icon={exporting?<LoadingOutlined/>:<FilePdfOutlined/>}
                onClick={handleExport} disabled={exporting}
                style={{background:'#0f172a',borderColor:'#0f172a',fontWeight:600}}>
                {exporting ? `Exporting… ${progress}%` : 'Download PDF'}
            </Button>
            {exporting && <Progress percent={progress} size="small" style={{width:110,marginBottom:0}} strokeColor="#60a5fa" showInfo={false}/>}
        </div>
    );
}


// --- ClausesByStandard: one radar+bar+table per standard, scores never mixed ---
function ClausesByStandard({ clauseData, standards, allClauses, selectedStdId }) {
    const grouped = useMemo(() => {
        const stdsToShow = selectedStdId
            ? standards.filter(s => s.id === selectedStdId)
            : standards;
        return stdsToShow.map(std => {
            const stdClauses = allClauses.filter(c => c.standard_id === std.id);
            const cm = {};
            clauseData.forEach(r => {
                if (!r.clause_code) return;
                const cl = stdClauses.find(c => c.code === r.clause_code);
                if (!cl) return;
                if (!cm[r.clause_code]) cm[r.clause_code] = { scores: [], title: r.clause_title || cl.title };
                cm[r.clause_code].scores.push(parseFloat(r.avg_score || r.total_score || 0));
            });
            const clauses = stdClauses.map(cl => ({
                code: cl.code,
                title: cm[cl.code]?.title || cl.title,
                avg: cm[cl.code]
                    ? cm[cl.code].scores.reduce((a, b) => a + b, 0) / cm[cl.code].scores.length
                    : null,
            }));
            return { std, clauses };
        }).filter(g => g.clauses.some(c => c.avg !== null));
    }, [clauseData, standards, allClauses, selectedStdId]);

    if (!grouped.length) return null;

    return (
        <>
        {grouped.map(({ std, clauses }) => {
            const validClauses = clauses.filter(c => c.avg !== null);
            const accent = std.accent || '#1890ff';
            const barOpt = {
                tooltip: { trigger: 'axis' },
                grid: { top:10, right:80, bottom:30, left:55 },
                xAxis: { type: 'value', max: 100, axisLabel: { formatter: v => `${v}%`, fontSize: 9 } },
                yAxis: { type: 'category', data: validClauses.map(c => c.code), axisLabel: { fontSize: 11, fontWeight: 700 } },
                series: [{ type: 'bar', barMaxWidth: 18,
                    data: validClauses.map(c => {
                        const r = getRating(c.avg);
                        return { value: parseFloat(c.avg.toFixed(1)), itemStyle: { color: r.color, borderRadius: [0,5,5,0] } };
                    }),
                    label: { show: true, position: 'right', formatter: p => `${p.value}%`, fontSize: 10 },
                }],
            };
            const radarOpt = validClauses.length >= 3 ? {
                tooltip: { trigger: 'item' },
                radar: {
                    indicator: validClauses.map(c => ({ name: c.code, max: 100 })),
                    radius: '65%', center: ['50%', '52%'],
                    axisName: { fontSize: 11, fontWeight: 700 }, splitNumber: 5,
                    splitArea: { areaStyle: { color: ['#f8f9fa', '#fff'] } },
                },
                series: [{ type: 'radar', data: [{
                    value: validClauses.map(c => parseFloat(c.avg.toFixed(1))),
                    name: std.name,
                    areaStyle: { color: `${accent}30` },
                    lineStyle: { color: accent, width: 2 },
                    itemStyle: { color: accent },
                }] }],
            } : null;
            return (
                <div key={std.id} style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        background: `${accent}12`, borderLeft: `5px solid ${accent}`,
                        borderRadius: '0 8px 8px 0', marginBottom: 12 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: accent,
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 900, fontSize: 14, flexShrink: 0 }}>
                            {std.name?.[0]?.toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: accent }}>{std.name}</div>
                            <div style={{ fontSize: 11, color: '#888' }}>{std.description || std.category || ''}</div>
                        </div>
                        <Tag style={{ background: accent, color: '#fff', border: 'none' }}>
                            {validClauses.length} clauses assessed
                        </Tag>
                    </div>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {radarOpt && (
                            <Col span={10}>
                                <XCard title={`${std.name} — Radar`}
                                       filename={`Radar_${(std.name||'').replace(/\s+/g,'_')}`}>
                                    <ReactECharts option={radarOpt} style={{ height: 260 }}/>
                                </XCard>
                            </Col>
                        )}
                        <Col span={radarOpt ? 14 : 24}>
                            <XCard title={`${std.name} — Clause Scores`}
                                   filename={`Clauses_${(std.name||'').replace(/\s+/g,'_')}`}>
                                <ReactECharts option={barOpt}
                                    style={{ height: Math.max(180, validClauses.length * 34 + 40) }}/>
                            </XCard>
                        </Col>
                    </Row>
                    <XCard title={`${std.name} — Clause Detail`}
                           filename={`Detail_${(std.name||'').replace(/\s+/g,'_')}`}
                           style={{ marginBottom: 8 }}>
                        <Table size="small" pagination={false} dataSource={validClauses} rowKey="code"
                            columns={[
                                { title: 'Code', dataIndex: 'code', width: 80,
                                  render: c => <Tag style={{ background: accent, color: '#fff', border: 'none', fontWeight: 700 }}>{c}</Tag> },
                                { title: 'Title', dataIndex: 'title', ellipsis: true },
                                { title: 'Avg Score', dataIndex: 'avg', width: 100, align: 'center',
                                  render: v => { if (v===null) return '—'; const r=getRating(v); return <span style={{ fontWeight:900, color:r.color }}>{v.toFixed(1)}%</span>; } },
                                { title: 'Stars', width: 90, align: 'center',
                                  render: (_,r) => { if (r.avg===null) return '—'; return <Stars count={getRating(r.avg).stars} size={11}/>; } },
                                { title: 'Rating', width: 150, align: 'center',
                                  render: (_,r) => { if (r.avg===null) return '—'; const rt=getRating(r.avg); return <Tag style={{ background:rt.color, color:'#fff', border:'none', fontSize:10 }}>{rt.label}</Tag>; } },
                                { title: 'Progress', width: 140,
                                  render: (_,r) => { if (r.avg===null) return '—'; const rt=getRating(r.avg); return <Progress percent={Math.min(100,r.avg)} strokeColor={rt.color} showInfo={false} size="small"/>; } },
                            ]}/>
                    </XCard>
                </div>
            );
        })}
        </>
    );
}

// ─── Period Comparison sub-component (up to 5 cycles) ───────────────────────
function DeltaBadge({ delta }) {
    if (delta === null || delta === undefined) return <span style={{color:'#ccc'}}>—</span>;
    const d = parseFloat(delta);
    if (Math.abs(d) < 0.1) return <Tag icon={<MinusOutlined/>} color="default" style={{fontSize:11}}>No change</Tag>;
    if (d > 0) return <Tag icon={<RiseOutlined/>} color="success" style={{fontSize:11,fontWeight:700}}>+{d.toFixed(1)}%</Tag>;
    return <Tag icon={<FallOutlined/>} color="error" style={{fontSize:11,fontWeight:700}}>{d.toFixed(1)}%</Tag>;
}

const CYCLE_COLORS = ['#1890ff','#52c41a','#fa8c16','#722ed1','#eb2f96'];

// Receives filters directly from the main dashboard — no own facility/level/clause filter
function PeriodComparison({ periods = [], filters = {}, standards = [], allClauses = [] }) {
    // Up to 5 selected period labels
    const [selected, setSelected] = useState([]);
    const [loading,  setLoading]  = useState(false);
    // dataMap: { [periodLabel]: [...clauseScoreRows] }
    const [dataMap,  setDataMap]  = useState({});
    // overallMap: { [periodLabel]: nationalAverageOrNull } -- sourced from
    // getComparisonReport, the same endpoint the main dashboard trusts.
    const [overallMap, setOverallMap] = useState({});

    // Default: select up to 5 most recent periods on mount
    useEffect(() => {
        if (periods.length > 0 && selected.length === 0) {
            setSelected(periods.slice(0, Math.min(5, periods.length)).map(p => p.label));
        }
    }, [periods]); // eslint-disable-line

    useEffect(() => {
        if (selected.length > 0) load();
    }, [selected, filters.facility_id, filters.level, filters.clause_id, filters.standard_id]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const results = await Promise.all(
                selected.map(label => {
                    const pd = periods.find(p => p.label === label);
                    const f  = {};
                    if (pd?.period_start) f.from_date    = pd.period_start;
                    if (pd?.period_end)   f.to_date      = pd.period_end;
                    f.period_label = label;
                    if (filters.facility_id) f.facility_id = filters.facility_id;
                    if (filters.level)       f.level       = filters.level;
                    if (filters.clause_id)   f.clause_id   = filters.clause_id;
                    return QoCService.getClauseScoresAcrossFacilities(f)
                        .then(r => ({ label, data: r.data?.data || [] }))
                        .catch(() => ({ label, data: [] }));
                })
            );
            const map = {};
            results.forEach(({ label, data }) => {
                // Segregate by standard — never mix scores across standards
                map[label] = filters.standard_id
                    ? data.filter(r => {
                        const cl = allClauses.find(c => c.code === r.clause_code);
                        return cl?.standard_id === filters.standard_id;
                    })
                    : data;
            });
            setDataMap(map);

            // Fetch the "overall" national-average score from getComparisonReport --
            // the SAME endpoint and SAME field (AVG of qoc_assessments.overall_percentage)
            // that the main Executive Dashboard uses for its "National Average Rating".
            // This guarantees the Period Comparison banner always matches the main
            // dashboard for the same period, rather than re-deriving a different number
            // from clause-score rows (which use a different source and can diverge).
            const overallResults = await Promise.all(
                selected.map(label => {
                    const cf = { period_label: label };
                    if (filters.facility_id) cf.facility_id = filters.facility_id;
                    if (filters.level)       cf.level       = filters.level;
                    return QoCService.getComparisonReport(cf)
                        .then(r => ({ label, comp: r.data?.data || [] }))
                        .catch(() => ({ label, comp: [] }));
                })
            );
            const oMap = {};
            overallResults.forEach(({ label, comp }) => {
                const scores = comp.map(f => parseFloat(f.average_percentage || 0)).filter(v => v > 0);
                oMap[label] = scores.length ? scores.reduce((a,b)=>a+b,0)/scores.length : null;
            });
            setOverallMap(oMap);
        } catch(e) { console.error(e); }
        finally   { setLoading(false); }
    };

    // Aggregate clause avg per period
    const clauseAvg = (data) => {
        const cm = {};
        data.forEach(r => {
            if (!r.clause_code) return;
            if (!cm[r.clause_code]) cm[r.clause_code] = [];
            cm[r.clause_code].push(parseFloat(r.avg_score || r.total_score || 0));
        });
        const out = {};
        Object.entries(cm).forEach(([code, scores]) => {
            out[code] = scores.reduce((a,b)=>a+b,0)/scores.length;
        });
        return out;
    };

    // Aggregate facility avg per period
    const facAvg = (data) => {
        const fm = {};
        data.forEach(r => {
            if (!r.facility_id) return;
            if (!fm[r.facility_id]) fm[r.facility_id] = { name: r.facility_name || r.facility_id, scores:[] };
            const s = parseFloat(r.avg_score || r.total_score || 0);
            if (s > 0) fm[r.facility_id].scores.push(s);
        });
        const out = {};
        Object.entries(fm).forEach(([fid, d]) => {
            out[fid] = { name: d.name, avg: d.scores.reduce((a,b)=>a+b,0)/Math.max(d.scores.length,1) };
        });
        return out;
    };

    // Clauses to display in charts — restricted to selected standard if active
    const displayClauses = useMemo(() => {
        let cls = filters.standard_id
            ? allClauses.filter(c => c.standard_id === filters.standard_id)
            : allClauses;
        if (filters.clause_id) {
            cls = cls.filter(c => c.id === filters.clause_id || c.id === parseInt(filters.clause_id));
        }
        return cls;
    }, [allClauses, filters.standard_id, filters.clause_id]);

    // Pre-compute per-period summaries
    const periodSummaries = selected.map((label, i) => {
        const data  = dataMap[label] || [];
        const cl    = clauseAvg(data);
        const fac   = facAvg(data);
        // Overall score is fetched from getComparisonReport via overallMap (see load()),
        // NOT re-derived from clause-score rows. This is the same source and same field
        // (AVG of qoc_assessments.overall_percentage) the main Executive Dashboard uses,
        // so the banner here always matches the main dashboard's number for the same
        // period instead of silently diverging due to different underlying queries.
        const overall = overallMap[label] != null ? overallMap[label] : null;
        return { label, color: CYCLE_COLORS[i], cl, fac, overall, hasData: data.length > 0 };
    });

    // ── Clause bar chart: grouped bars, one group per clause ──────────────────
    const clauseBarOpt = () => ({
        tooltip: { trigger:'axis' },
        legend: {
            data: selected,
            bottom: 0,
            textStyle: { fontSize:11 },
        },
        grid: { top:20, right:20, bottom:55, left:55 },
        xAxis: { type:'category', data: displayClauses.map(c=>c.code), axisLabel:{fontSize:10} },
        yAxis: { type:'value', min:0, max:100, axisLabel:{formatter:v=>`${v}%`,fontSize:10} },
        series: periodSummaries.map(ps => ({
            name:       ps.label,
            type:       'bar',
            barMaxWidth: Math.max(8, Math.floor(40 / Math.max(selected.length,1))),
            data:       displayClauses.map(c => ({
                value:     parseFloat((ps.cl[c.code]||0).toFixed(1)),
                itemStyle: { color: ps.color, borderRadius:[3,3,0,0] },
            })),
            label: { show: selected.length <= 3, position:'top', fontSize:8,
                formatter: p => p.value > 0 ? `${p.value}%` : '' },
        })),
    });

    // ── Overall trend line ─────────────────────────────────────────────────────
    const trendLineOpt = () => ({
        tooltip: { trigger:'axis' },
        legend: { data: selected, bottom:0, textStyle:{fontSize:11} },
        grid:   { top:20, right:20, bottom:55, left:55 },
        xAxis:  { type:'category', data: displayClauses.map(c=>c.code), axisLabel:{fontSize:10} },
        yAxis:  { type:'value', min:0, max:100, axisLabel:{formatter:v=>`${v}%`,fontSize:10} },
        series: periodSummaries.map(ps => ({
            name:      ps.label,
            type:      'line',
            smooth:    true,
            symbolSize: 6,
            data:      displayClauses.map(c => parseFloat((ps.cl[c.code]||0).toFixed(1))),
            lineStyle: { color: ps.color, width:2 },
            itemStyle: { color: ps.color },
            areaStyle: { color: ps.color, opacity: 0.06 },
        })),
    });

    // ── Facility delta vs first selected period ────────────────────────────────
    const facilityDeltaOpt = () => {
        if (periodSummaries.length < 2) return {};
        const base = periodSummaries[0];
        // Collect all fids that appear in at least base + one other
        const allFids = new Set(Object.keys(base.fac));
        periodSummaries.slice(1).forEach(ps => Object.keys(ps.fac).forEach(f => allFids.add(f)));

        const rows = [...allFids].map(fid => {
            const baseScore = base.fac[fid]?.avg;
            const name = (base.fac[fid] || periodSummaries.find(ps=>ps.fac[fid])?.fac[fid])?.name || fid;
            return { fid, name, baseScore };
        }).filter(r => r.baseScore > 0);

        return {
            tooltip: { trigger:'axis' },
            legend: { data: selected.slice(1), bottom:0, textStyle:{fontSize:11} },
            grid:   { top:10, right:80, bottom:45, left:150 },
            xAxis:  { type:'value', axisLabel:{formatter:v=>`${v>0?'+':''}${v.toFixed(1)}%`,fontSize:9} },
            yAxis:  { type:'category', data:rows.map(r=>r.name.slice(0,22)).reverse(), axisLabel:{fontSize:9} },
            series: periodSummaries.slice(1).map((ps, i) => ({
                name:       ps.label,
                type:       'bar',
                barMaxWidth: 10,
                data: rows.map(r => {
                    const delta = ps.fac[r.fid]?.avg != null ? ps.fac[r.fid].avg - r.baseScore : null;
                    return delta !== null
                        ? { value: parseFloat(delta.toFixed(1)), itemStyle:{ color: ps.color, borderRadius: delta>=0?[0,4,4,0]:[4,0,0,4] } }
                        : { value: 0, itemStyle:{ opacity:0 } };
                }).reverse(),
                label: { show:true, position:'right', fontSize:8,
                    formatter: p => p.value !== 0 ? `${p.value>0?'+':''}${p.value}%` : '' },
            })),
        };
    };

    // ── Clause table: one column per period ───────────────────────────────────
    const clauseTableCols = [
        { title:'Clause', dataIndex:'clause', width:70,
          render:(c,row)=><Tag style={{background:row.color,color:'#fff',border:'none',fontWeight:700}}>{c}</Tag> },
        { title:'Standard', dataIndex:'stdName', width:110, ellipsis:true,
          render:(t,row)=><span style={{color:row.color,fontWeight:600,fontSize:11}}>{t}</span> },
        { title:'Title', dataIndex:'title', ellipsis:true },
        ...periodSummaries.map((ps, i) => ({
            title: <span style={{color:ps.color,fontWeight:700}}>{ps.label}</span>,
            dataIndex: `score_${i}`,
            width: 100,
            align: 'center',
            sorter: (a,b) => (a[`score_${i}`]||0)-(b[`score_${i}`]||0),
            render: v => v != null
                ? <div>
                    <span style={{fontWeight:900,color:getRating(v).color,fontSize:13}}>{v.toFixed(1)}%</span>
                    <div><Stars count={getRating(v).stars} size={9}/></div>
                  </div>
                : <span style={{color:'#ccc'}}>—</span>,
        })),
        // Change col: last period vs first period
        ...(periodSummaries.length >= 2 ? [{
            title: <span style={{color:'#888'}}>
                Δ {periodSummaries[periodSummaries.length-1].label.slice(-7)} vs {periodSummaries[0].label.slice(-7)}
            </span>,
            dataIndex: 'delta_last_first',
            width: 120,
            align: 'center',
            sorter: (a,b) => (a.delta_last_first||0)-(b.delta_last_first||0),
            render: v => <DeltaBadge delta={v}/>,
        }] : []),
    ];

    const clauseTableData = displayClauses.map(cl => {
        const std = standards.find(s => s.id === cl.standard_id);
        const row = { key:cl.id||cl.code, clause:cl.code, title:cl.title, color:std?.accent||'#1890ff', stdName: std?.name||'—' };
        periodSummaries.forEach((ps, i) => {
            row[`score_${i}`] = ps.cl[cl.code] ?? null;
        });
        const first = periodSummaries[0]?.cl[cl.code];
        const last  = periodSummaries[periodSummaries.length-1]?.cl[cl.code];
        row.delta_last_first = (first!=null && last!=null) ? last - first : null;
        return row;
    }).filter(row => periodSummaries.some((ps,i) => row[`score_${i}`] !== null));

    // ── Facility table: one column per period ─────────────────────────────────
    const allFacIds = new Set(periodSummaries.flatMap(ps => Object.keys(ps.fac)));
    const facTableData = [...allFacIds].map(fid => {
        const row = { key:fid };
        const realName = periodSummaries.map(ps => ps.fac[fid]?.name).find(n => n) || fid;
        row.name = realName;
        periodSummaries.forEach((ps, i) => {
            row[`score_${i}`] = ps.fac[fid]?.avg ?? null;
        });
        const scoredPeriods = periodSummaries
            .map((ps, i) => ({ i, avg: ps.fac[fid]?.avg }))
            .filter(p => p.avg != null);
        if (scoredPeriods.length >= 2) {
            const first = scoredPeriods[0].avg;
            const last  = scoredPeriods[scoredPeriods.length-1].avg;
            row.delta = last - first;
        } else {
            row.delta = null;
        }
        return row;
    }).sort((a,b)=>(b[`score_0`]||0)-(a[`score_0`]||0));

    const facTableCols = [
        { title:'Facility', dataIndex:'name', ellipsis:true, width:200 },
        ...periodSummaries.map((ps, i) => ({
            title: <span style={{color:ps.color,fontWeight:700}}>{ps.label}</span>,
            dataIndex: `score_${i}`,
            width: 100,
            align: 'center',
            render: v => v != null
                ? <span style={{fontWeight:700,color:getRating(v).color}}>{v.toFixed(1)}%</span>
                : <span style={{color:'#ccc'}}>—</span>,
        })),
        ...(periodSummaries.length >= 2 ? [{
            title: 'Trend',
            dataIndex: 'delta',
            width: 120,
            align: 'center',
            sorter: (a,b) => (a.delta||0)-(b.delta||0),
            defaultSortOrder: 'descend',
            render: v => <DeltaBadge delta={v}/>,
        }] : []),
    ];

    const hasAnyData = periodSummaries.some(ps => ps.hasData);
    const filterHints = [
        filters.standard_id && `standard: ${standards.find(s=>s.id===filters.standard_id)?.name||''}`,
        filters.facility_id && 'facility',
        filters.level       && `level: ${filters.level}`,
        filters.clause_id   && 'clause',
    ].filter(Boolean);

    return (
        <Spin spinning={loading}>
            {/* Compact period selector row — up to 5 cycles */}
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',
                marginBottom:16,padding:'10px 16px',background:'#f8faff',
                borderRadius:8,border:'1px solid #e8f0fe'}}>
                <span style={{fontSize:12,color:'#888',fontWeight:600,whiteSpace:'nowrap'}}>
                    Cycles to compare:
                </span>
                {[0,1,2,3,4].map(i => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:4}}>
                        <span style={{
                            width:16,height:16,borderRadius:'50%',flexShrink:0,
                            background: selected[i] ? CYCLE_COLORS[i] : '#e0e0e0',
                            display:'inline-block'
                        }}/>
                        <Select
                            size="small"
                            style={{width:150}}
                            value={selected[i] || null}
                            placeholder={`Cycle ${i+1}`}
                            allowClear={i > 0}
                            onChange={val => {
                                const next = [...selected];
                                if (val) {
                                    next[i] = val;
                                } else {
                                    next.splice(i, 1);
                                }
                                setSelected(next.filter(Boolean));
                            }}>
                            {periods.map(p => (
                                <Option key={p.label} value={p.label}
                                    disabled={selected.includes(p.label) && selected[i] !== p.label}>
                                    {p.is_current==1?'★ ':''}{p.label}
                                </Option>
                            ))}
                        </Select>
                    </div>
                ))}
                {filterHints.length > 0 && (
                    <Tag color="geekblue" style={{marginLeft:4}}>🔗 {filterHints.join(' · ')}</Tag>
                )}
            </div>

            {!hasAnyData && !loading && (
                <Alert message="Select periods above to compare. Facility, level, and clause filters from the main bar apply automatically." type="info" showIcon/>
            )}

            {hasAnyData && (
                <>
                {/* Overall score banners */}
                <Row gutter={[12,12]} style={{marginBottom:16}}>
                    {periodSummaries.map((ps, i) => {
                        const r = ps.overall !== null ? getRating(ps.overall) : null;
                        return (
                            <Col key={ps.label} span={Math.floor(24/Math.max(periodSummaries.length,1))}>
                                <Card size="small" style={{borderTop:`4px solid ${ps.color}`,textAlign:'center'}}>
                                    <div style={{fontSize:11,color:ps.color,fontWeight:700,marginBottom:4}}>{ps.label}</div>
                                    {ps.overall !== null ? (
                                        <>
                                            <div style={{fontSize:28,fontWeight:900,color:r.color,lineHeight:1}}>{ps.overall.toFixed(1)}%</div>
                                            <div style={{margin:'4px 0'}}><Stars count={r.stars} size={12}/></div>
                                            <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10}}>{r.label}</Tag>
                                        </>
                                    ) : <div style={{color:'#ccc',padding:'12px 0',fontSize:11}}>No data</div>}
                                </Card>
                            </Col>
                        );
                    })}
                    {/* Change card: last vs first */}
                    {periodSummaries.length >= 2 &&
                        periodSummaries[0].overall !== null &&
                        periodSummaries[periodSummaries.length-1].overall !== null && (
                        <Col span={Math.floor(24/Math.max(periodSummaries.length,1))}>
                            <Card size="small" style={{borderTop:'4px solid #722ed1',textAlign:'center'}}>
                                <div style={{fontSize:10,color:'#888',fontWeight:700,marginBottom:4}}>
                                    Δ {periodSummaries[0].label} → {periodSummaries[periodSummaries.length-1].label}
                                </div>
                                {(() => {
                                    const delta = periodSummaries[periodSummaries.length-1].overall - periodSummaries[0].overall;
                                    return (
                                        <>
                                            <div style={{fontSize:28,fontWeight:900,lineHeight:1,
                                                color:delta>=0?'#52c41a':'#f5222d'}}>
                                                {delta>=0?'+':''}{delta.toFixed(1)}%
                                            </div>
                                            <div style={{marginTop:6,fontSize:11,color:'#666'}}>
                                                {delta>=0?'📈 Improvement':'📉 Regression'}
                                            </div>
                                        </>
                                    );
                                })()}
                            </Card>
                        </Col>
                    )}
                </Row>

                {/* Clause grouped bar chart */}
                <XCard title="Clause Scores — All Cycles" filename="Cycles_Clause_Bar" style={{marginBottom:16}}>
                    <ReactECharts option={clauseBarOpt()} style={{height:320}}/>
                </XCard>

                {/* Trend lines per clause */}
                <XCard title="Clause Trend Lines — Across Cycles" filename="Cycles_Clause_Trend" style={{marginBottom:16}}>
                    <ReactECharts option={trendLineOpt()} style={{height:280}}/>
                </XCard>

                {/* Clause table */}
                <XCard title="Clause-by-Clause — All Cycles" filename="Cycles_Clause_Table" style={{marginBottom:16}}>
                    <Table size="small" dataSource={clauseTableData} rowKey="key"
                        pagination={false} scroll={{x:'max-content'}}
                        columns={clauseTableCols}/>
                </XCard>

                {/* Facility delta vs first period */}
                {periodSummaries.length >= 2 && Object.keys(periodSummaries[0].fac).length > 0 && (
                    <XCard title={`Facility Change vs ${periodSummaries[0].label}`} filename="Cycles_Facility_Delta" style={{marginBottom:16}}>
                        <div style={{fontSize:11,color:'#888',marginBottom:8}}>
                            Delta of each subsequent cycle compared to <strong>{periodSummaries[0].label}</strong> (baseline)
                        </div>
                        <ReactECharts option={facilityDeltaOpt()} style={{height:Math.max(250, Math.min(facTableData.length,20)*20+80)}}/>
                    </XCard>
                )}

                {/* Facility table */}
                <XCard title="Facility Scores — All Cycles" filename="Cycles_Facility_Table">
                    <Table size="small" dataSource={facTableData} rowKey="key"
                        pagination={{pageSize:20,size:'small'}}
                        scroll={{x:'max-content'}}
                        columns={facTableCols}/>
                </XCard>
                </>
            )}
        </Spin>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function QoCExecutiveDashboard() {
    const dashboardRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [facilities, setFacilities] = useState([]);
    const [standards,  setStandards]  = useState([]);  // all standards
    const [allClauses, setAllClauses] = useState([]);  // all clauses across all standards
    const [clauses, setClauses]       = useState([]);
    const [dateRange, setDateRange]   = useState([moment().subtract(12,'months'), moment()]);
    const [filters, setFilters] = useState({
        facility_id: null, level: null, clause_id: null, standard_id: null, period_label: null,
        from_date: moment().subtract(12,'months').format('YYYY-MM-DD'),
        to_date:   moment().format('YYYY-MM-DD'),
    });
    const [reportFacility, setReportFacility] = useState(null);

    const [overview,   setOverview]   = useState({});
    const [trendData,  setTrend]      = useState([]);
    const [compData,   setComp]       = useState([]);
    const [distData,   setDist]       = useState([]);
    const [schedData,  setSched]      = useState({ data:[], summary:{} });
    const [clauseData, setClause]     = useState([]);
    const [subData,    setSub]        = useState([]);
    const [meData,     setME]         = useState([]);
    const [kpiData,    setKPI]        = useState({ kpis:[], actuals:[] });
    const [qiData,     setQI]         = useState([]);
    const [surveyData, setSurveyData] = useState({ surveys:[], stats:{}, by_facility:{} });
    const [periods,    setPeriods]    = useState([]);
    const [heatmapPage, setHeatmapPage] = useState(0); // 0-indexed page for clause heatmap pagination

    useEffect(() => {
        trailPageView(TRAIL_MODULES.EXECUTIVE_DASH, window.location.pathname);
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data||[]));
        QoCService.getStandards().then(r => setStandards(r.data?.data||[]));
        QoCService.getClauses().then(r => {
            const cls = r.data?.data || [];
            setAllClauses(cls);
            setClauses(cls);
        });
        QoCService.getPeriods().then(r => {
            const active = (r.data?.data||[]).filter(p => parseInt(p.is_active) === 1);
            setPeriods(active);
            const current = active.find(p => parseInt(p.is_current) === 1);
            if (current) {
                setFilters(f => ({ ...f, period_label: current.label, from_date: current.period_start, to_date: current.period_end }));
                setDateRange([moment(current.period_start), moment(current.period_end)]);
            }
        });
    }, []);

    useEffect(() => { load(); setHeatmapPage(0); }, [filters]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        const f = {
            from_date:  filters.from_date,
            to_date:    filters.to_date,
            ...(filters.facility_id  && { facility_id:  filters.facility_id }),
            ...(filters.level        && { level:        filters.level }),
            ...(filters.clause_id    && { clause_id:    filters.clause_id }),
            ...(filters.standard_id  && { standard_id:  filters.standard_id }),
            ...(filters.period_label && { period_label: filters.period_label }),
        };
        const kf = {
            ...(filters.period_label && { period_label: filters.period_label }),
            ...(filters.from_date    && { from_date:    filters.from_date }),
            ...(filters.to_date      && { to_date:      filters.to_date }),
            ...(filters.facility_id  && { facility_id:  filters.facility_id }),
        };
        try {
            const results = await Promise.allSettled([
                QoCService.getComparisonReport(f),
                QoCService.getComplianceTrends(filters.facility_id, 12),
                QoCService.getPerformanceDistribution(f),
                QoCService.getAuditSchedules(f),
                QoCService.getClauseScoresAcrossFacilities(f),
                QoCService.getKpiVsActual(kf),
                QoCService.getQualityIndex(kf),
                QoCService.getCustomerSurveys({ ...f, period_label: filters.period_label }),
            ]);
            const ok = (r, key='data') => r.status==='fulfilled' ? r.value.data?.[key]||[] : [];
           const comp           = ok(results[0]);
            const pendingReview  = results[0].status==='fulfilled' ? results[0].value.data?.pending_review||[] : [];
            const pendingCount   = pendingReview.reduce((sum,r)=>sum+parseInt(r.count||0),0);
            const draftCount     = results[0].status==='fulfilled' ? results[0].value.data?.draft_count||0 : 0;
            const trends    = ok(results[1]);
            const dist      = ok(results[2]);
            const schedRes  = results[3].status==='fulfilled' ? results[3].value.data||{} : {};
            const clauseRaw = ok(results[4]);
            const kpi       = results[5].status==='fulfilled' ? results[5].value.data?.data||{} : {};
            const qi        = ok(results[6]);
            const surveyRes = results[7]?.status==='fulfilled' ? results[7].value.data||{} : {};

            // Segregate by standard — filter BEFORE setting state
            // so all subsequent computations (subData, meData) are scoped correctly
            let clauseRawFiltered = clauseRaw;
            // 1. Filter by standard
            if (filters.standard_id) {
                const stdCodes = new Set(allClauses.filter(c => c.standard_id === filters.standard_id).map(c => c.code));
                clauseRawFiltered = clauseRawFiltered.filter(r => stdCodes.has(r.clause_code));
            }
            // 2. Filter by specific clause
            if (filters.clause_id) {
                const selClause = allClauses.find(c => c.id === filters.clause_id || c.id === parseInt(filters.clause_id));
                if (selClause) clauseRawFiltered = clauseRawFiltered.filter(r => r.clause_code === selClause.code);
            }
            // 3. Filter by facility
            if (filters.facility_id) {
                clauseRawFiltered = clauseRawFiltered.filter(r => String(r.facility_id) === String(filters.facility_id));
            }
            // 4. Filter by level
            if (filters.level) {
                clauseRawFiltered = clauseRawFiltered.filter(r => r.site_type === filters.level);
            }

            const today     = moment().startOf('day');
            const allScheds = schedRes.data || [];
            const overdueScheds = allScheds.filter(s => s.status!=='completed' && s.status!=='cancelled' && moment(s.scheduled_date).isBefore(today));
            // Apply facility + level filters to comparison data on the frontend
            let compFiltered = comp;
            if (filters.facility_id) {
                compFiltered = compFiltered.filter(f => String(f.facility_id) === String(filters.facility_id));
            }
            if (filters.level) {
                compFiltered = compFiltered.filter(f => f.facility_level === filters.level);
            }
            const avg = compFiltered.length ? compFiltered.reduce((s,f)=>s+parseFloat(f.average_percentage||0),0)/compFiltered.length : 0;

            setOverview({
                avg_score:          parseFloat(avg.toFixed(1)),
                assessed_count:     comp.filter(f=>f.assessment_count>0).length,
                high_performers:    comp.filter(f=>parseFloat(f.average_percentage)>=90).length,
                needs_attention:    comp.filter(f=>parseFloat(f.average_percentage)<70).length,
                overdue_count:      overdueScheds.length,
                overdue_scheds:     overdueScheds,
                schedule_total:     allScheds.length,
                schedule_completed: allScheds.filter(s=>s.status==='completed').length,
                schedule_inprog:    allScheds.filter(s=>s.status==='in_progress').length,
                pending_review_count: pendingCount,
                draft_count: draftCount,
            });
            setTrend(trends);
            setComp(compFiltered.sort((a,b)=>parseFloat(b.average_percentage)-parseFloat(a.average_percentage)));
            setDist(dist);
            setSched({ data: allScheds, summary: schedRes.summary||{} });
            setClause(clauseRawFiltered);
            setKPI(kpi);
            setQI(qi);
            setSurveyData({
                surveys:     surveyRes.data||[],
                stats:       surveyRes.stats||{},
                by_facility: (surveyRes.data||[]).reduce((acc,s)=>{
                    if (!acc[s.facility_id]) acc[s.facility_id]={name:s.facility_name,ip:[],op:[]};
                    if (s.survey_type==='inpatient') acc[s.facility_id].ip.push(s);
                    else acc[s.facility_id].op.push(s);
                    return acc;
                },{}),
            });
            const sm = {};
            clauseRawFiltered.forEach(r => {
                if (!sm[r.clause_code]) sm[r.clause_code]={code:r.clause_code,title:r.clause_title,scores:[],stdId:allClauses.find(c=>c.code===r.clause_code)?.standard_id};
                sm[r.clause_code].scores.push(parseFloat(r.avg_score||r.total_score||0));
            });
            setSub(Object.values(sm).map(s=>({...s, avg:s.scores.length?s.scores.reduce((a,b)=>a+b,0)/s.scores.length:0, count:s.scores.length})).sort((a,b)=>a.avg-b.avg));
            setME(clauseRawFiltered.filter(r=>parseFloat(r.avg_score||r.total_score||0)<70).sort((a,b)=>parseFloat(a.avg_score||a.total_score||0)-parseFloat(b.avg_score||b.total_score||0)).slice(0,30));
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    const sf = (k,v) => setFilters(f=>({...f,[k]:v}));
    const scrollTo = (id) => { const el=document.getElementById(id); if(el) el.scrollIntoView({behavior:'smooth',block:'start'}); };

    // ─── Chart options ────────────────────────────────────────────────────────
    const trendOpt = () => ({
        tooltip:{trigger:'axis',backgroundColor:'rgba(15,15,35,0.92)',textStyle:{color:'#fff'}},
        legend:{data:['Avg Score','Target 90%','Benchmark 85%'],textStyle:{fontSize:11},bottom:0},
        grid:{top:20,right:20,bottom:50,left:48},
        xAxis:{type:'category',data:trendData.map(t=>moment(t.month).format('MMM YY')),axisLabel:{fontSize:10,rotate:30}},
        yAxis:{type:'value',min:0,max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:10}},
        series:[
            {name:'Avg Score',type:'line',smooth:true,symbolSize:5,
             data:trendData.map(t=>parseFloat(t.avg_compliance||0).toFixed(1)),
             lineStyle:{width:3,color:'#1890ff'},itemStyle:{color:'#1890ff'},
             areaStyle:{color:{type:'linear',x:0,y:0,x2:0,y2:1,colorStops:[{offset:0,color:'rgba(24,144,255,0.28)'},{offset:1,color:'rgba(24,144,255,0.02)'}]}}},
            {name:'Target 90%',type:'line',data:Array(trendData.length).fill(90),symbol:'none',lineStyle:{type:'dashed',color:'#52c41a',width:2},itemStyle:{color:'#52c41a'}},
            {name:'Benchmark 85%',type:'line',data:Array(trendData.length).fill(85),symbol:'none',lineStyle:{type:'dashed',color:'#faad14',width:2},itemStyle:{color:'#faad14'}},
        ]
    });
    const distPieOpt = () => ({
        tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
        legend:{orient:'vertical',right:0,top:'center',textStyle:{fontSize:10}},
        series:[{type:'pie',radius:['42%','68%'],center:['42%','50%'],
            data:distData.filter(d=>d.facility_count>0).map(d=>({value:d.facility_count,name:d.range_label,itemStyle:{color:d.color}})),
            label:{show:true,formatter:'{b}\n{c}',fontSize:10},emphasis:{itemStyle:{shadowBlur:8}}}]
    });
    const facilityBarOpt = () => {
        const d=compData.slice(0,25);
        return {
            tooltip:{trigger:'axis'},
            grid:{top:10,right:60,bottom:30,left:160},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:d.map(f=>(f.facility_name||'').slice(0,26)).reverse(),axisLabel:{fontSize:9}},
            series:[{type:'bar',barMaxWidth:14,
                data:d.map(f=>{const v=parseFloat(f.average_percentage||0);const r=getRating(v);return{value:parseFloat(v.toFixed(1)),itemStyle:{color:r.color,borderRadius:[0,4,4,0]}};}).reverse(),
                label:{show:true,position:'right',formatter:p=>`${p.value}%`,fontSize:9}}]
        };
    };
    const schedDonutOpt = () => {
        const today=moment().startOf('day'); const all=schedData.data;
        const counts={
            completed:   all.filter(s=>s.status==='completed').length,
            in_progress: all.filter(s=>s.status==='in_progress').length,
            scheduled:   all.filter(s=>s.status==='scheduled'&&!moment(s.scheduled_date).isBefore(today)).length,
            overdue:     all.filter(s=>s.status!=='completed'&&s.status!=='cancelled'&&moment(s.scheduled_date).isBefore(today)).length,
            cancelled:   all.filter(s=>s.status==='cancelled').length,
        };
        return {
            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
            legend:{bottom:0,textStyle:{fontSize:10}},
            series:[{type:'pie',radius:['45%','72%'],center:['50%','44%'],
                data:[
                    {value:counts.completed,  name:'Completed',  itemStyle:{color:'#52c41a'}},
                    {value:counts.in_progress,name:'In Progress',itemStyle:{color:'#faad14'}},
                    {value:counts.scheduled,  name:'Scheduled',  itemStyle:{color:'#1890ff'}},
                    {value:counts.overdue,    name:'Overdue',    itemStyle:{color:'#f5222d'}},
                    {value:counts.cancelled,  name:'Cancelled',  itemStyle:{color:'#bbb'}},
                ].filter(d=>d.value>0),
                label:{formatter:'{b}\n{c}',fontSize:10}}]
        };
    };
    const clauseRadarOpt = () => {
        if (!clauseData.length) return {};
        const cm={};
        clauseData.forEach(r=>{if(!cm[r.clause_code])cm[r.clause_code]=[];cm[r.clause_code].push(parseFloat(r.avg_score||r.total_score||0));});
        const cl=CLAUSES.map(c=>({code:c.code,avg:cm[c.code]?cm[c.code].reduce((a,b)=>a+b,0)/cm[c.code].length:0}));
        return {
            tooltip:{trigger:'item'},
            radar:{indicator:cl.map(c=>({name:c.code,max:100})),radius:'65%',center:['50%','52%'],axisName:{fontSize:11,fontWeight:700},splitNumber:5,splitArea:{areaStyle:{color:['#f8f9fa','#fff']}}},
            series:[{type:'radar',data:[{value:cl.map(c=>parseFloat(c.avg.toFixed(1))),name:'Avg Score',areaStyle:{color:'rgba(24,144,255,0.18)'},lineStyle:{color:'#1890ff',width:2},itemStyle:{color:'#1890ff'}}]}]
        };
    };
    const clauseBarOpt = () => {
        const cm={};
        clauseData.forEach(r=>{if(!cm[r.clause_code])cm[r.clause_code]=[];cm[r.clause_code].push(parseFloat(r.avg_score||r.total_score||0));});
        const data=CLAUSES.map(c=>{const scores=cm[c.code]||[];const avg=scores.length?scores.reduce((a,b)=>a+b,0)/scores.length:0;return{code:c.code,avg:parseFloat(avg.toFixed(1)),color:c.color};}).sort((a,b)=>a.avg-b.avg);
        return {
            tooltip:{trigger:'axis'},
            grid:{top:10,right:60,bottom:30,left:55},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:data.map(d=>d.code),axisLabel:{fontSize:11,fontWeight:700}},
            series:[{type:'bar',barMaxWidth:18,data:data.map(d=>({value:d.avg,itemStyle:{color:d.color,borderRadius:[0,5,5,0]}})),label:{show:true,position:'right',formatter:p=>`${p.value}%`,fontSize:10}}]
        };
    };
    const gaugeOpt = (target, actual) => ({
        series:[{type:'gauge',radius:'90%',
            axisLine:{lineStyle:{width:14,color:[[Math.min(actual/100,1),'#1890ff'],[1,'#f0f0f0']]}},
            pointer:{show:false},axisTick:{show:false},splitLine:{show:false},axisLabel:{show:false},
            progress:{show:true,width:14},
            detail:{valueAnimation:true,formatter:v=>`${v}%\n▸${target}%`,color:actual>=target?'#52c41a':'#f5222d',fontSize:11,fontWeight:700,offsetCenter:[0,0]},
            data:[{value:parseFloat(actual.toFixed(1))}]
        }]
    });
    const qiBarOpt = () => {
        const sorted=[...qiData].sort((a,b)=>parseFloat(b.quality_index)-parseFloat(a.quality_index)).slice(0,15);
        return {
            tooltip:{trigger:'axis'},
            legend:{data:['QI Score','QoC Score'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:20,bottom:40,left:140},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:sorted.map(q=>(q.facility_name||'').slice(0,22)).reverse(),axisLabel:{fontSize:9}},
            series:[
                {name:'QI Score',type:'bar',barMaxWidth:10,
                 data:sorted.map(q=>{const r=getRating(parseFloat(q.quality_index||0));return{value:parseFloat(q.quality_index||0).toFixed(1),itemStyle:{color:r.color,borderRadius:[0,4,4,0]}};}).reverse(),
                 label:{show:true,position:'right',fontSize:9}},
                {name:'QoC Score',type:'bar',barMaxWidth:10,
                 data:sorted.map(q=>({value:parseFloat(q.qoc_score||0).toFixed(1),itemStyle:{color:'rgba(24,144,255,0.28)',borderRadius:[0,4,4,0]}})).reverse()},
            ]
        };
    };

    const today = moment().startOf('day');
    const NAVLINKS = [
        {id:'sec-overview',label:'Overview',icon:'📊'},{id:'sec-trend',label:'Trend',icon:'📈'},
        {id:'sec-schedules',label:'Schedules',icon:'📅'},{id:'sec-clauses',label:filters.standard_id&&standards.find(s=>s.id===filters.standard_id)?`${standards.find(s=>s.id===filters.standard_id).name?.slice(0,8)} Clauses`:'Clauses',icon:'🔵'},
        {id:'sec-substandards',label:'Sub-Standards',icon:'🔷'},{id:'sec-mes',label:'Low MEs',icon:'⚠'},
        {id:'sec-kpis',label:'KPI vs Actuals',icon:'🎯'},{id:'sec-qindex',label:'Quality Index',icon:'⭐'},
        {id:'sec-surveys',label:'Survey Dashboard',icon:'📝'},{id:'sec-comparison',label:'Period Comparison',icon:'🔄'},
    ];
    const goToAwards = () => window.open('/qoc/awards', '_blank');
    const surveyScoreColor = s => s>=75?'#52c41a':s>=50?'#fa8c16':'#f5222d';
    const surveyBarOpt = () => {
        const byFac=Object.values(surveyData.by_facility); if(!byFac.length) return {};
        const names=byFac.map(f=>(f.name||'').slice(0,22));
        const ipAvgs=byFac.map(f=>!f.ip.length?null:parseFloat((f.ip.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/f.ip.length).toFixed(1)));
        const opAvgs=byFac.map(f=>!f.op.length?null:parseFloat((f.op.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/f.op.length).toFixed(1)));
        return {
            tooltip:{trigger:'axis'},legend:{data:['Inpatient Avg','Outpatient Avg'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:60,bottom:40,left:160},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:names,axisLabel:{fontSize:10}},
            series:[
                {name:'Inpatient Avg',type:'bar',barMaxWidth:12,data:ipAvgs.map(v=>v===null?{value:0,itemStyle:{opacity:0}}:{value:v,itemStyle:{color:'#0050b3',borderRadius:[0,4,4,0]}}),label:{show:true,position:'right',fontSize:9,formatter:p=>p.value>0?`${p.value}%`:''}},
                {name:'Outpatient Avg',type:'bar',barMaxWidth:12,data:opAvgs.map(v=>v===null?{value:0,itemStyle:{opacity:0}}:{value:v,itemStyle:{color:'#006837',borderRadius:[0,4,4,0]}}),label:{show:true,position:'right',fontSize:9,formatter:p=>p.value>0?`${p.value}%`:''}},
            ],
        };
    };
    const surveyDistOpt = (type) => {
        const surveys=surveyData.surveys.filter(s=>s.survey_type===type); if(!surveys.length) return {};
        const buckets={'≥75 Good':0,'50–74 Fair':0,'<50 Poor':0};
        surveys.forEach(s=>{const v=parseFloat(s.overall_score||0);if(v>=75)buckets['≥75 Good']++;else if(v>=50)buckets['50–74 Fair']++;else buckets['<50 Poor']++;});
        const colors={'≥75 Good':'#52c41a','50–74 Fair':'#fa8c16','<50 Poor':'#f5222d'};
        return {
            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
            series:[{type:'pie',radius:['42%','68%'],center:['50%','48%'],
                data:Object.entries(buckets).filter(([,v])=>v>0).map(([k,v])=>({name:k,value:v,itemStyle:{color:colors[k]}})),
                label:{formatter:'{b}\n{c}',fontSize:10}}],
        };
    };
    const surveyTimelineOpt = () => {
        const sorted=[...surveyData.surveys].sort((a,b)=>moment(a.survey_date).unix()-moment(b.survey_date).unix());
        const ip=sorted.filter(s=>s.survey_type==='inpatient'); const op=sorted.filter(s=>s.survey_type==='outpatient');
        return {
            tooltip:{trigger:'axis'},legend:{data:['Inpatient','Outpatient'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:20,bottom:40,left:48},
            xAxis:{type:'category',data:[...new Set(sorted.map(s=>moment(s.survey_date).format('DD MMM YY')))],axisLabel:{fontSize:9,rotate:30}},
            yAxis:{type:'value',min:0,max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            series:[
                {name:'Inpatient',type:'scatter',symbolSize:10,data:ip.map(s=>[moment(s.survey_date).format('DD MMM YY'),parseFloat(s.overall_score||0).toFixed(1)]),itemStyle:{color:'#0050b3'}},
                {name:'Outpatient',type:'scatter',symbolSize:10,data:op.map(s=>[moment(s.survey_date).format('DD MMM YY'),parseFloat(s.overall_score||0).toFixed(1)]),itemStyle:{color:'#006837'}},
            ],
        };
    };

    return (
        <>
        <div className="page-content" ref={dashboardRef} data-pdf-root="true">

            {/* ── Dark header ── */}
            <div className="page-head uc d-flex" style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',padding:'14px 24px',marginBottom:0}}>
                <div className="my-auto" style={{display:'flex',alignItems:'center',gap:12}}>
                    <BarChartOutlined style={{fontSize:22,color:'#60a5fa'}}/>
                    <div>
                        <h2 style={{margin:0,color:'#fff',fontSize:18,fontWeight:800}}>Executive Dashboard</h2>
                        <div style={{fontSize:11,color:'rgba(255,255,255,0.45)'}}>Eswatini Quality of Care — Health Standards Management</div>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{display:'flex',alignItems:'center',gap:12}}>
                    <span style={{fontSize:11,color:'rgba(255,255,255,0.35)'}}>{moment().format('DD MMM YYYY HH:mm')}</span>
                    
                    <ExecDashPDF contentRef={dashboardRef} filters={filters}/>
                </div>
            </div>

            {/* ── STICKY FILTER + NAV BAR ── */}
            <div style={{position:'sticky',top:0,zIndex:100,background:'#fff',borderBottom:'2px solid #e8f0fe',boxShadow:'0 2px 12px rgba(0,0,0,0.08)'}}>
                <div style={{padding:'10px 20px',borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',background:'#f8faff'}}>
                    <FilterOutlined style={{color:'#94a3b8',fontSize:13}}/>
                    <span style={{fontSize:10,fontWeight:700,color:'#94a3b8',letterSpacing:1,marginRight:4}}>FILTERS</span>
                    {/* Standard — primary filter; isolates scores per standard */}
                    <Select allowClear size="small" placeholder="All Standards" style={{minWidth:180}}
                        value={filters.standard_id}
                        onChange={v => setFilters(f => ({ ...f, standard_id: v||null, clause_id: null }))}>
                        {standards.map(s => (
                            <Option key={s.id} value={s.id}>
                                <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                                    <span style={{width:10,height:10,borderRadius:'50%',background:s.accent||'#1890ff',display:'inline-block',flexShrink:0}}/>
                                    {s.name}
                                </span>
                            </Option>
                        ))}
                    </Select>
                    <Select allowClear size="small" placeholder="All Facilities" style={{minWidth:180}} showSearch optionFilterProp="children" value={filters.facility_id} onChange={v=>sf('facility_id',v)}>
                        {facilities.map(f=><Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                    </Select>
                    <Select allowClear size="small" placeholder="All Levels" style={{width:110}} value={filters.level} onChange={v=>sf('level',v)}>
                        {LEVELS.map(l=><Option key={l} value={l}>{l}</Option>)}
                    </Select>
                    {/* Clause — grouped by standard when all standards showing */}
                    <Select allowClear size="small" placeholder="All Clauses" style={{width:200}} value={filters.clause_id} onChange={v=>sf('clause_id',v)}>
                        {filters.standard_id
                            ? allClauses.filter(c=>c.standard_id===filters.standard_id).map(c => {
                                const std = standards.find(s=>s.id===c.standard_id);
                                return <Option key={c.id} value={c.id}>
                                    <span style={{fontWeight:700,marginRight:4,color:std?.accent||'#1890ff'}}>{c.code}</span>
                                    <span style={{fontSize:11,color:'#888'}}>{(c.title||'').slice(0,22)}</span>
                                </Option>;
                            })
                            : standards.map(std => {
                                const sc = allClauses.filter(c=>c.standard_id===std.id);
                                if (!sc.length) return null;
                                return (
                                    <OptGroup key={std.id} label={<span style={{fontWeight:700,color:std.accent||'#555'}}>{std.name}</span>}>
                                        {sc.map(c=>(
                                            <Option key={c.id} value={c.id}>
                                                <span style={{fontWeight:700,marginRight:4,color:std.accent||'#1890ff'}}>{c.code}</span>
                                                <span style={{fontSize:11,color:'#888'}}>{(c.title||'').slice(0,22)}</span>
                                            </Option>
                                        ))}
                                    </OptGroup>
                                );
                            }).filter(Boolean)
                        }
                    </Select>
                    <RangePicker size="small" style={{width:220}} value={dateRange}
                        onChange={v=>{setDateRange(v||[moment().subtract(12,'months'),moment()]);sf('from_date',(v?.[0]||moment().subtract(12,'months')).format('YYYY-MM-DD'));sf('to_date',(v?.[1]||moment()).format('YYYY-MM-DD'));}}/>
                    <Select allowClear size="small" placeholder="Assessment Period" style={{width:180}} value={filters.period_label}
                        onChange={v=>{sf('period_label',v);const pd=periods.find(p=>p.label===v);if(pd){setDateRange([moment(pd.period_start),moment(pd.period_end)]);sf('from_date',pd.period_start);sf('to_date',pd.period_end);}}}>
                        {periods.map(p=><Option key={p.label} value={p.label}>{p.is_current==1&&'★ '}{p.label}</Option>)}
                    </Select>
                    <Button size="small" icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                    {(filters.facility_id||filters.level||filters.clause_id||filters.period_label)&&(
                        <Button size="small" type="link" style={{color:'#f5222d',padding:0}} onClick={()=>setFilters(f=>({...f,facility_id:null,level:null,clause_id:null,standard_id:null,period_label:null}))}>Clear filters ×</Button>
                    )}
                </div>
                <div style={{padding:'0 20px',display:'flex',gap:0,overflowX:'auto'}}>
                    {NAVLINKS.map(n=>(
                        <div key={n.id} onClick={()=>scrollTo(n.id)}
                            style={{padding:'8px 14px',cursor:'pointer',fontSize:12,fontWeight:600,color:'#555',whiteSpace:'nowrap',borderBottom:'3px solid transparent',transition:'all 0.15s',userSelect:'none',display:'flex',alignItems:'center',gap:4}}
                            onMouseEnter={e=>{e.currentTarget.style.color='#1890ff';e.currentTarget.style.borderBottomColor='#1890ff';}}
                            onMouseLeave={e=>{e.currentTarget.style.color='#555';e.currentTarget.style.borderBottomColor='transparent';}}>
                            {n.icon} {n.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Active standard banner */}
            {filters.standard_id && standards.find(s=>s.id===filters.standard_id) && (() => {
                const std = standards.find(s => s.id === filters.standard_id);
                return (
                    <div style={{ padding:'6px 20px', background:`${std.accent}12`,
                        borderBottom:`2px solid ${std.accent}40`,
                        display:'flex', alignItems:'center', gap:10, fontSize:12 }}>
                        <span style={{ width:12, height:12, borderRadius:'50%', background:std.accent, display:'inline-block' }}/>
                        <span style={{ fontWeight:700, color:std.accent }}>Showing: {std.name}</span>
                        <span style={{ color:'#888' }}>— all charts and scores filtered to this standard's clauses only. Scores are NOT mixed with other standards.</span>
                        <Tag style={{ background:std.accent, color:'#fff', border:'none', fontSize:10, marginLeft:'auto' }}>
                            {allClauses.filter(c=>c.standard_id===std.id).length} clauses
                        </Tag>
                    </div>
                );
            })()}
            {/* ── SCROLLABLE CONTENT ── */}
            <div style={{background:'#f4f6fb',padding:'0 24px 40px'}}>
                <Spin spinning={loading}>

                {/* ════ SECTION 1 — OVERVIEW ════ */}
                <SectionTitle id="sec-overview" icon={<BarChartOutlined/>} title="Overview" subtitle="National QoC performance at a glance" color="#1890ff"/>
                <Row gutter={[12,12]} style={{marginBottom:16}}>
                    {[
                        {label:'National Avg Score',   value:overview.avg_score||0,   suffix:'%', color:'#1890ff', icon:'📊'},
                        {label:'Facilities Assessed',  value:overview.assessed_count||0,           color:'#52c41a', icon:'🏥'},
                        {label:'High Performers ≥90%', value:overview.high_performers||0,          color:'#389e0d', icon:'🏆'},
                        {label:'Needs Attention <70%', value:overview.needs_attention||0,          color:'#f5222d', icon:'⚠️'},
                        {label:'Pending Approval',  value:overview.pending_review_count||0, sub:'Submitted, not yet approved', color:'#722ed1', icon:'🔔'},
                        {label:'Draft Assessments',   value:overview.draft_count||0,          sub:'Started but not yet submitted',  color:'#fa8c16', icon:'📝'},
                        {label:'Overdue Schedules',    value:overview.overdue_count||0,  sub:'Scheduled date past, not completed', color:'#fa8c16', icon:'⏰'},
                        {label:'Total Schedules',      value:overview.schedule_total||0, sub:`${overview.schedule_completed||0} completed`, color:'#722ed1', icon:'📅'},
                    ].map(k=>(<Col key={k.label} xs={12} sm={8} md={4}><KpiCard {...k}/></Col>))}
                </Row>

                {overview.avg_score > 0 && (()=>{
                    const r=getRating(overview.avg_score);
                    return (
                        <div style={{background:`linear-gradient(135deg,${r.color}14,${r.color}06)`,border:`2px solid ${r.color}40`,borderRadius:14,padding:'20px 28px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
                            <div>
                                <div style={{fontSize:11,color:'#888',fontWeight:700,letterSpacing:1,marginBottom:4}}>NATIONAL AVERAGE RATING</div>
                                <div style={{fontSize:52,fontWeight:900,color:r.color,lineHeight:1}}>{overview.avg_score}%</div>
                                <div style={{marginTop:8}}><Stars count={r.stars} size={22}/></div>
                            </div>
                            <div style={{textAlign:'center'}}>
                                <Progress type="circle" percent={Math.min(100,overview.avg_score)} width={110} strokeColor={r.color} format={()=><span style={{fontWeight:900,fontSize:18,color:r.color}}>{overview.avg_score}%</span>}/>
                                <div style={{fontWeight:800,fontSize:16,color:r.color,marginTop:8}}>{r.label}</div>
                            </div>
                            <div>{RATING.map(r2=>(
                                <div key={r2.label} style={{display:'flex',alignItems:'center',gap:8,marginBottom:5,opacity:overview.avg_score>=r2.min?1:0.3}}>
                                    <Stars count={r2.stars} size={11}/>
                                    <span style={{fontSize:11,color:r2.color,fontWeight:700,minWidth:140}}>{r2.label}</span>
                                    <span style={{fontSize:10,color:'#aaa'}}>{r2.min}–{r2.max}%</span>
                                </div>
                            ))}</div>
                        </div>
                    );
                })()}

                <Row gutter={16} style={{marginBottom:8}}>
                    <Col span={12}>
                        <XCard title={<span style={{color:'#389e0d'}}>🏆 Top 10 Facilities</span>} filename="Top_10_Facilities">
                            {compData.slice(0,10).map((f,i)=>{
                                const r=getRating(parseFloat(f.average_percentage||0));
                                return (
                                    <div key={f.facility_id||i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f5f5f5'}}>
                                        <span style={{width:22,fontSize:10,color:'#aaa',flexShrink:0}}>#{i+1}</span>
                                        <div style={{flex:1,fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.facility_name}</div>
                                        <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10,fontWeight:700}}>{parseFloat(f.average_percentage||0).toFixed(1)}%</Tag>
                                        <Stars count={r.stars} size={10}/>
                                        <span onClick={()=>setReportFacility({id:f.facility_id,business_name:f.facility_name,site_type:f.facility_level,located_region:f.region})}
                                            style={{cursor:'pointer',color:'#f5222d',fontSize:10,flexShrink:0,padding:'1px 6px',border:'1px solid #ffccc7',borderRadius:4}}>PDF</span>
                                    </div>
                                );
                            })}
                        </XCard>
                    </Col>
                    <Col span={12}>
                        <XCard title={<span style={{color:'#f5222d'}}>⚠ Needs Attention (Bottom 10)</span>} filename="Needs_Attention">
                            {[...compData].reverse().slice(0,10).map((f,i)=>{
                                const r=getRating(parseFloat(f.average_percentage||0));
                                return (
                                    <div key={f.facility_id||i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f5f5f5'}}>
                                        <div style={{flex:1,fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.facility_name}</div>
                                        <span style={{fontSize:10,color:'#aaa',flexShrink:0}}>{f.facility_level||'—'}</span>
                                        <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10,fontWeight:700}}>{parseFloat(f.average_percentage||0).toFixed(1)}%</Tag>
                                        <span onClick={()=>setReportFacility({id:f.facility_id,business_name:f.facility_name,site_type:f.facility_level,located_region:f.region})}
                                            style={{cursor:'pointer',color:'#f5222d',fontSize:10,flexShrink:0,padding:'1px 6px',border:'1px solid #ffccc7',borderRadius:4}}>PDF</span>
                                    </div>
                                );
                            })}
                        </XCard>
                    </Col>
                </Row>

                {/* ════ SECTION 2 — TREND & DISTRIBUTION ════ */}
                <SectionTitle id="sec-trend" icon={<LineChartOutlined/>} title="Compliance Trend & Distribution" subtitle="12-month national trend with targets, and facility score distribution" color="#0ea5e9"/>
                <Row gutter={16} style={{marginBottom:16}}>
                    <Col span={16}>
                        <XCard title="National Compliance Trend (12 months)" filename="National_Compliance_Trend">
                            {trendData.length ? <ReactECharts option={trendOpt()} style={{height:280}}/> : <Empty description="No trend data"/>}
                        </XCard>
                    </Col>
                    <Col span={8}>
                        <XCard title="Facility Distribution by Score Range" filename="Score_Distribution">
                            {distData.some(d=>d.facility_count>0) ? <ReactECharts option={distPieOpt()} style={{height:280}}/> : <Empty description="No data"/>}
                        </XCard>
                    </Col>
                </Row>
                <XCard title="Facility Score Comparison — Top 25" filename="Facility_Score_Comparison" style={{marginBottom:16}}>
                    {compData.length ? <ReactECharts option={facilityBarOpt()} style={{height:Math.max(300,Math.min(compData.length,25)*18+60)}}/> : <Empty description="No facility assessment data"/>}
                </XCard>

                {/* ════ SECTION 3 — SCHEDULES ════ */}
                <SectionTitle id="sec-schedules" icon={<CalendarOutlined/>} title="Audit Schedules" subtitle="Overdue = scheduled date has passed and assessment not completed" color="#f59e0b"/>
                <Row gutter={[12,12]} style={{marginBottom:16}}>
                    {[
                        {label:'Total Schedules',               value:schedData.data.length,                                                                                          color:'#1890ff',icon:'📅'},
                        {label:'Completed',                     value:schedData.data.filter(s=>s.status==='completed').length,                                                        color:'#52c41a',icon:'✅'},
                        {label:'In Progress',                   value:schedData.data.filter(s=>s.status==='in_progress').length,                                                      color:'#faad14',icon:'🔄'},
                        {label:'Upcoming (not yet due)',         value:schedData.data.filter(s=>s.status==='scheduled'&&!moment(s.scheduled_date).isBefore(today)).length,             color:'#06b6d4',icon:'📆'},
                        {label:'Overdue (past date, not done)', value:overview.overdue_count||0, sub:'Based on scheduled_date vs today',                                              color:'#f5222d',icon:'⏰'},
                        {label:'Cancelled',                     value:schedData.data.filter(s=>s.status==='cancelled').length,                                                        color:'#999',   icon:'✗'},
                    ].map(k=>(<Col key={k.label} xs={12} sm={8} md={4}><KpiCard {...k}/></Col>))}
                </Row>
                <Row gutter={16} style={{marginBottom:16}}>
                    <Col span={8}>
                        <XCard title="Schedule Status Breakdown" filename="Schedule_Status">
                            {schedData.data.length ? <ReactECharts option={schedDonutOpt()} style={{height:280}}/> : <Empty description="No schedules found"/>}
                        </XCard>
                    </Col>
                    <Col span={16}>
                        <XCard title={<span><WarningFilled style={{color:'#f5222d',marginRight:6}}/>Overdue Schedules ({overview.overdue_count||0})</span>} filename="Overdue_Schedules">
                            <Table size="small"
                                dataSource={schedData.data.filter(s=>s.status!=='completed'&&s.status!=='cancelled'&&moment(s.scheduled_date).isBefore(today))}
                                rowKey="id" pagination={{pageSize:8,size:'small'}}
                                columns={[
                                    {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:180},
                                    {title:'Clause',dataIndex:'clause_code',width:70,render:c=>c?<Tag color="blue" style={{fontSize:10}}>{c}</Tag>:<Tag>Full</Tag>},
                                    {title:'Sched. Date',dataIndex:'scheduled_date',width:100,render:d=><span style={{color:'#f5222d',fontWeight:700}}>{moment(d).format('DD MMM YY')}</span>},
                                    {title:'Days Overdue',width:100,render:(_,r)=>{const days=today.diff(moment(r.scheduled_date),'days');return <Tag color="red" style={{fontSize:10}}>{days}d overdue</Tag>;}},
                                    {title:'Lead Assessor',dataIndex:'lead_assessor_name',ellipsis:true,width:130},
                                    {title:'Status',dataIndex:'status',width:100,render:s=>{const cfg=SCHED_STATUS[s]||SCHED_STATUS.scheduled;return <Tag color={cfg.color} style={{fontSize:10}}>{cfg.label}</Tag>;}},
                                ]}/>
                        </XCard>
                    </Col>
                </Row>
                <XCard title="All Schedules" filename="All_Schedules" style={{marginBottom:16}}>
                    <Table size="small" dataSource={schedData.data} rowKey="id" pagination={{pageSize:12,size:'small'}}
                        columns={[
                            {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                            {title:'Clause',dataIndex:'clause_code',width:70,render:c=>c?<Tag color="blue" style={{fontSize:10}}>{c}</Tag>:<Tag>Full</Tag>},
                            {title:'Scheduled Date',dataIndex:'scheduled_date',width:115,render:(d,r)=>{
                                const isOverdue=r.status!=='completed'&&r.status!=='cancelled'&&moment(d).isBefore(today);
                                return <span style={{color:isOverdue?'#f5222d':'inherit',fontWeight:isOverdue?700:400}}>{moment(d).format('DD MMM YY')}{isOverdue&&<Tag color="red" style={{marginLeft:4,fontSize:9}}>OVERDUE</Tag>}</span>;}},
                            {title:'Lead Assessor',dataIndex:'lead_assessor_name',ellipsis:true,width:140},
                            {title:'Status',dataIndex:'status',width:110,render:s=>{const cfg=SCHED_STATUS[s]||SCHED_STATUS.scheduled;return <Tag color={cfg.color} style={{fontSize:10}}>{cfg.label}</Tag>;}},
                            {title:'Assessment Score',dataIndex:'assessment_score',width:110,align:'center',render:v=>v?<span style={{fontWeight:700}}>{parseFloat(v).toFixed(1)}%</span>:'—'},
                        ]}/>
                </XCard>
{/* Empty state when standard filter returns no data */}
{filters.standard_id && clauseData.length === 0 && !loading && (
    <Alert
        style={{marginBottom:16}}
        message={`No assessment data found for ${standards.find(s=>s.id===filters.standard_id)?.name || 'selected standard'}`}
        description="There are no approved assessments for this standard in the selected date range."
        type="info" showIcon/>
)}
                {/* ════ SECTION 4 — CLAUSES grouped by standard (scores never mixed) ════ */}
                <SectionTitle id="sec-clauses" icon={<PieChartOutlined/>}
                    title={filters.standard_id && standards.find(s=>s.id===filters.standard_id)
                        ? `${standards.find(s=>s.id===filters.standard_id).name} — Clause Performance`
                        : 'Clause Performance by Standard'}
                    subtitle={filters.standard_id
                        ? `Showing ${standards.find(s=>s.id===filters.standard_id)?.name} clauses only`
                        : 'Each standard shown separately — scores are never mixed across standards'}
                    color="#8b5cf6"/>
                {(() => {
                    // Build the full facility list once (not capped) so pagination has something to page through
                    const heatmapClausesList = (() => {
                        let cls = filters.standard_id
                            ? allClauses.filter(c => c.standard_id === filters.standard_id)
                            : allClauses;
                        if (filters.clause_id) cls = cls.filter(c => c.id === filters.clause_id || c.id === parseInt(filters.clause_id));
                        return cls;
                    })();
                    const allFacRows = Object.entries(clauseData.reduce((acc,r)=>{
                        if(!acc[r.facility_id]) acc[r.facility_id]={name:r.facility_name||'—',scores:{}};
                        acc[r.facility_id].scores[r.clause_code]=parseFloat(r.avg_score||r.total_score||0);
                        return acc;
                    },{}));
                    const PAGE_SIZE = 25;
                    const totalPages = Math.max(1, Math.ceil(allFacRows.length / PAGE_SIZE));
                    const safePage = Math.min(heatmapPage, totalPages - 1);
                    const pageRows = allFacRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
                    const startN = allFacRows.length ? safePage * PAGE_SIZE + 1 : 0;
                    const endN   = Math.min(allFacRows.length, safePage * PAGE_SIZE + PAGE_SIZE);

                    return (
                        <XCard title="Clause Score Heatmap — by Facility" filename="Clause_Heatmap" style={{marginBottom:16}}
                            extra={
                                <span style={{fontSize:11,color:'#888',marginRight:8}}>
                                    {allFacRows.length>0
                                        ? `Showing ${startN}–${endN} of ${allFacRows.length} facilities`
                                        : 'No facilities'}
                                </span>
                            }>
                            <div style={{overflowX:'auto'}}>
                                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                                    <thead>
                                        <tr style={{background:'#f0f5ff'}}>
                                            <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid #dbeafe',minWidth:170}}>Facility</th>
                                            {heatmapClausesList.map(c => {
                                                const std = standards.find(s=>s.id===c.standard_id);
                                                return (
                                                    <th key={c.id} style={{padding:'6px 8px',textAlign:'center',borderBottom:'1px solid #dbeafe',color:std?.accent||'#1890ff',fontWeight:800,minWidth:52}}>
                                                        <div>{c.code}</div>
                                                        {!filters.standard_id && <div style={{fontSize:8,color:'#aaa',fontWeight:400}}>{std?.name?.slice(0,5)}</div>}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map(([fid,fac],i)=>(
                                            <tr key={fid} style={{background:i%2?'#fafafe':'#fff'}}>
                                                <td style={{padding:'5px 10px',fontWeight:600,fontSize:11,maxWidth:170,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fac.name}</td>
                                                {heatmapClausesList.map(c=>{
                                                    const v=fac.scores[c.code]; const r=v!=null?getRating(v):null;
                                                    return <td key={c.id} style={{padding:'3px 4px',textAlign:'center'}}>
                                                        {v!=null?<div style={{background:r.color,color:'#fff',borderRadius:4,padding:'2px 3px',fontWeight:700,fontSize:10}}>{v.toFixed(0)}%</div>:<span style={{color:'#ddd'}}>—</span>}
                                                    </td>;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginTop:14}}>
                                    <Button size="small" disabled={safePage===0}
                                        onClick={()=>setHeatmapPage(p=>Math.max(0,p-1))}>
                                        ← Prev
                                    </Button>
                                    <span style={{fontSize:12,color:'#555',fontWeight:600}}>
                                        Page {safePage+1} of {totalPages}
                                    </span>
                                    <Button size="small" disabled={safePage>=totalPages-1}
                                        onClick={()=>setHeatmapPage(p=>Math.min(totalPages-1,p+1))}>
                                        Next →
                                    </Button>
                                </div>
                            )}
                        </XCard>
                    );
                })()}

                {/* Per-standard clause performance — one section per standard */}
                <ClausesByStandard
                    clauseData={clauseData}
                    standards={standards}
                    allClauses={allClauses}
                    selectedStdId={filters.standard_id}
                />

                {/* ════ SECTION 5 — SUB-STANDARDS ════ */}
                <SectionTitle id="sec-substandards" icon={<ApartmentOutlined/>}
                    title="Clause Summary Cards"
                    subtitle="Per-clause performance grouped by standard — scores are never mixed across standards"
                    color="#10b981"/>
                {standards.filter(std => !filters.standard_id || std.id === filters.standard_id).map(std => {
                    const stdSubs = subData.filter(s => s.stdId === std.id);
                    if (!stdSubs.length) return null;
                    return (
                        <div key={std.id} style={{ marginBottom: 20 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:std.accent||'#555', marginBottom:10,
                                padding:'6px 12px', background:`${std.accent||'#1890ff'}10`,
                                borderRadius:6, display:'inline-flex', alignItems:'center', gap:6 }}>
                                <span style={{ width:10, height:10, borderRadius:'50%', background:std.accent||'#1890ff', display:'inline-block' }}/>
                                {std.name}
                            </div>
                            <Row gutter={[14,14]} style={{ marginBottom:16 }}>
                                {stdSubs.map(s => {
                                    const r = getRating(s.avg);
                                    return (
                                        <Col key={s.code} span={6}>
                                            <div style={{border:`2px solid ${r.color}40`,borderRadius:12,padding:'16px 18px',background:`${r.color}07`,height:'100%'}}>
                                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                                                    <div>
                                                        <div style={{fontWeight:900,fontSize:14,color:std.accent||'#333'}}>{s.code}</div>
                                                        <div style={{fontSize:10,color:'#888',marginTop:2,lineHeight:1.5}}>{(s.title||'').slice(0,36)}</div>
                                                    </div>
                                                    <div style={{fontSize:24,fontWeight:900,color:r.color,lineHeight:1}}>{s.avg.toFixed(1)}%</div>
                                                </div>
                                                <Progress percent={Math.min(100,s.avg)} strokeColor={r.color} showInfo={false} size="small"/>
                                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:6}}>
                                                    <Stars count={r.stars} size={12}/>
                                                    <span style={{fontSize:10,color:'#aaa'}}>{s.count} assessments</span>
                                                </div>
                                                <div style={{marginTop:4,fontSize:10,fontWeight:700,color:r.color}}>{r.label}</div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    );
                })}
                {subData.length===0 && <Empty description="No clause data for selected filters"/>}

                {/* ════ SECTION 6 — LOW MEs ════ */}
                <SectionTitle id="sec-mes" icon={<TableOutlined/>} title="Low-Performing Clause Results" subtitle="Clause assessments scoring below 70% — requires immediate attention" color="#ef4444"/>
                {meData.length===0
                    ? <Alert message="No clause results below 70% for the selected filters." type="success" showIcon/>
                    : (
                        <XCard title={`${meData.length} clause results below 70%`} filename="Low_Performing_MEs">
                            <Table size="small" dataSource={meData} rowKey={(r,i)=>i} pagination={{pageSize:20,size:'small'}}
                                columns={[
                                    {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                    {title:'Clause',dataIndex:'clause_code',width:65,render:c=>{const cl=CLAUSES.find(x=>x.code===c);return <Tag style={{background:cl?.color||'#1890ff',color:'#fff',fontSize:10,border:'none'}}>{c}</Tag>;}},
                                    {title:'Date',dataIndex:'assessment_date',width:90,render:d=>d?moment(d).format('DD MMM YY'):'—'},
                                    {title:'✓ Met',dataIndex:'met_count',width:55,align:'center',render:v=><Tag color="green" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'◑ Partial',dataIndex:'partially_met_count',width:65,align:'center',render:v=><Tag color="orange" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'✗ Not Met',dataIndex:'not_met_count',width:70,align:'center',render:v=><Tag color="red" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'Score',width:80,align:'center',render:(_,row)=>{const v=row.avg_score||row.total_score||0;const r=getRating(parseFloat(v||0));return <span style={{fontWeight:900,color:r.color}}>{parseFloat(v||0).toFixed(1)}%</span>;}},
                                    {title:'Rating',dataIndex:'interpretation',width:150,render:v=>{const r=RATING.find(x=>x.label===v)||RATING[5];return <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10}}>{v||'—'}</Tag>;}},
                                ]}/>
                        </XCard>
                    )}

                {/* ════ SECTION 7 — KPI vs ACTUALS ════ */}
                <SectionTitle id="sec-kpis" icon={<TrophyOutlined/>} title="KPI Targets vs Actuals" subtitle="Performance against defined targets by clause and assessment period" color="#faad14"/>
                {(!kpiData.kpis||kpiData.kpis.length===0)
                    ? <Alert message="No KPI targets defined. Set targets in the KPI Targets module." type="info" showIcon/>
                    : (
                        <>
                            <Row gutter={[12,12]} style={{marginBottom:16}}>
                                {(kpiData.kpis||[]).filter(k=>k.kpi_type==='target').slice(0,6).map((kpi,i)=>{
                                    const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));
                                    const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:0;
                                    const met=avg>=parseFloat(kpi.target_pct||0);
                                    return (
                                        <Col key={i} span={4}>
                                            <Card size="small" bodyStyle={{padding:'10px 8px'}} style={{border:`2px solid ${met?'#52c41a':'#f5222d'}40`,background:met?'#f6ffed':'#fff2f0'}}>
                                                <div style={{fontSize:10,color:'#888',marginBottom:4,textAlign:'center'}}>{kpi.clause_code||'Overall'} • {kpi.period_label}</div>
                                                <ReactECharts option={gaugeOpt(kpi.target_pct,avg)} style={{height:110}}/>
                                                <div style={{textAlign:'center',fontSize:10,fontWeight:700,color:met?'#52c41a':'#f5222d'}}>{met?'✓ Target Met':'✗ Below Target'}</div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                            <XCard title="KPI Target vs Actual — Full Table" filename="KPI_vs_Actual" style={{marginBottom:16}}>
                                <Table size="small" dataSource={(kpiData.kpis||[]).filter(k=>k.kpi_type==='target')} rowKey={(r,i)=>i} pagination={{pageSize:15,size:'small'}}
                                    columns={[
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=><Tag color="blue">{p}</Tag>},
                                        {title:'Clause',dataIndex:'clause_code',width:80,render:c=>c?<Tag>{c}</Tag>:<Tag color="purple">Overall</Tag>},
                                        {title:'Level',dataIndex:'facility_level',width:100,render:l=>l||'All Levels'},
                                        {title:'Target',dataIndex:'target_pct',width:80,render:v=><strong style={{color:'#1890ff'}}>{v}%</strong>},
                                        {title:'Actual',width:80,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:null;return avg!=null?<span style={{fontWeight:700,color:avg>=kpi.target_pct?'#52c41a':'#f5222d'}}>{avg.toFixed(1)}%</span>:<span style={{color:'#ccc'}}>No data</span>;}},
                                        {title:'Gap',width:80,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:null;if(avg==null)return<span style={{color:'#ccc'}}>—</span>;const d=avg-parseFloat(kpi.target_pct);return<Tag color={d>=0?'success':'error'}>{d>=0?'+':''}{d.toFixed(1)}%</Tag>;}},
                                        {title:'Progress',width:130,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:0;const pct=parseFloat(kpi.target_pct)||1;return<Progress percent={Math.min(100,(avg/pct)*100)} size="small" strokeColor={avg>=pct?'#52c41a':'#f5222d'} showInfo={false}/>;}},
                                    ]}/>
                            </XCard>
                        </>
                    )}

                {/* ════ SECTION 8 — QUALITY INDEX ════ */}
                <SectionTitle id="sec-qindex" icon={<StarFilled style={{color:'#faad14'}}/>} title="Quality Index" subtitle="Composite QoC assessment + customer survey weighted index per facility" color="#7c3aed"/>
                {qiData.length===0
                    ? <Alert message="No Quality Index data. Calculate from the Quality Index module." type="info" showIcon/>
                    : (
                        <>
                            <Row gutter={16} style={{marginBottom:16}}>
                                <Col span={16}>
                                    <XCard title="Quality Index — Facilities Ranked" filename="Quality_Index_Ranked">
                                        <ReactECharts option={qiBarOpt()} style={{height:Math.max(300,Math.min(qiData.length,15)*22+60)}}/>
                                    </XCard>
                                </Col>
                                <Col span={8}>
                                    <XCard title="QI Rating Distribution" filename="QI_Rating_Distribution">
                                        <ReactECharts option={{
                                            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
                                            series:[{type:'pie',radius:['40%','70%'],center:['50%','50%'],
                                                data:RATING.map(r=>({name:r.label,value:qiData.filter(q=>parseFloat(q.quality_index||0)>=r.min&&parseFloat(q.quality_index||0)<=r.max).length,itemStyle:{color:r.color}})).filter(d=>d.value>0),
                                                label:{formatter:'{b}\n{c}',fontSize:10}}]
                                        }} style={{height:300}}/>
                                    </XCard>
                                </Col>
                            </Row>
                            <XCard title="Quality Index — Full Table" filename="Quality_Index_Table" style={{marginBottom:16}}>
                                <Table size="small" dataSource={[...qiData].sort((a,b)=>parseFloat(b.quality_index)-parseFloat(a.quality_index))} rowKey="id" pagination={{pageSize:20,size:'small'}}
                                    columns={[
                                        {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=><Tag color="blue">{p}</Tag>},
                                        {title:'QoC%',dataIndex:'qoc_score',width:75,align:'center',render:v=><span style={{fontWeight:700,color:'#1890ff'}}>{parseFloat(v||0).toFixed(1)}%</span>},
                                        {title:'Survey%',width:110,align:'center',render:(_,r)=>{const composite=parseFloat(r.survey_composite||0);const ipScore=parseFloat(r.inpatient_score||0);const opScore=parseFloat(r.outpatient_score||0);const total=parseInt(r.inpatient_count||0)+parseInt(r.outpatient_count||0);return(<div><span style={{fontWeight:700,color:total===0?'#f5222d':'#52c41a'}}>{composite.toFixed(1)}%</span>{total>0&&<div style={{fontSize:10,color:'#aaa',marginTop:2}}>IP:{ipScore.toFixed(0)} OP:{opScore.toFixed(0)} ({total})</div>}{total===0&&<div style={{fontSize:9,color:'#f5222d'}}>no surveys</div>}</div>);}},
                                        {title:'IP',dataIndex:'inpatient_count',width:45,align:'center'},
                                        {title:'OP',dataIndex:'outpatient_count',width:45,align:'center'},
                                        {title:'QI Score',dataIndex:'quality_index',width:85,align:'center',render:v=>{const r=getRating(parseFloat(v||0));return<span style={{fontWeight:900,color:r.color,fontSize:14}}>{parseFloat(v||0).toFixed(1)}%</span>;}},
                                        {title:'Stars',dataIndex:'star_rating',width:85,align:'center',render:v=><Stars count={v||0} size={12}/>},
                                        {title:'Rating',dataIndex:'interpretation',width:140,render:v=>{const r=RATING.find(x=>x.label===v)||RATING[5];return<Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10}}>{v||'—'}</Tag>;}},
                                        {title:'Weights',width:100,align:'center',render:(_,r)=><span style={{fontSize:10,color:'#888'}}>{r.qoc_weight||80}/{r.survey_weight||20}%</span>},
                                    ]}/>
                            </XCard>
                        </>
                    )}

                {/* ════ SECTION 9 — SURVEY DASHBOARD ════ */}
                <SectionTitle id="sec-surveys" icon="📝" title="Customer Satisfaction Survey Dashboard" subtitle="Inpatient and outpatient survey scores by facility and period" color="#3b82f6"/>
                {surveyData.surveys.length===0
                    ? <Alert message="No survey data found for the selected filters." type="info" showIcon/>
                    : (
                        <>
                            {(()=>{
                                const st=surveyData.stats||{};
                                const avg=parseFloat(st.average_score||0);
                                const satPct=(st.total_surveys||0)>0?Math.round((st.satisfied_count/st.total_surveys)*100):0;
                                return (
                                    <Row gutter={[12,12]} style={{marginBottom:16}}>
                                        {[
                                            {label:'Total Surveys',      value:st.total_surveys||0,   color:'#3b82f6',icon:'📝'},
                                            {label:'Inpatient',          value:st.inpatient_count||0, color:'#0050b3',icon:'🛏'},
                                            {label:'Outpatient',         value:st.outpatient_count||0,color:'#006837',icon:'🚶'},
                                            {label:'Avg Score',          value:`${avg.toFixed(1)}%`,  color:surveyScoreColor(avg),icon:'⭐'},
                                            {label:'Satisfied / Better', value:`${satPct}%`, sub:'≥70% score threshold', color:satPct>=70?'#52c41a':'#fa8c16',icon:'😊'},
                                        ].map(k=>(<Col key={k.label} span={k.sub?6:4}><KpiCard {...k}/></Col>))}
                                    </Row>
                                );
                            })()}
                            <Row gutter={16} style={{marginBottom:16}}>
                                <Col span={12}>
                                    <XCard title="IP vs OP Average Score by Facility" filename="Survey_IP_vs_OP">
                                        {Object.keys(surveyData.by_facility).length>0 ? <ReactECharts option={surveyBarOpt()} style={{height:Math.max(200,Object.keys(surveyData.by_facility).length*36+60)}}/> : <Empty description="No data"/>}
                                    </XCard>
                                </Col>
                                <Col span={6}>
                                    <XCard title="Inpatient Score Distribution" filename="Survey_IP_Distribution">
                                        {surveyData.surveys.some(s=>s.survey_type==='inpatient') ? <ReactECharts option={surveyDistOpt('inpatient')} style={{height:220}}/> : <Empty description="No inpatient surveys"/>}
                                    </XCard>
                                </Col>
                                <Col span={6}>
                                    <XCard title="Outpatient Score Distribution" filename="Survey_OP_Distribution">
                                        {surveyData.surveys.some(s=>s.survey_type==='outpatient') ? <ReactECharts option={surveyDistOpt('outpatient')} style={{height:220}}/> : <Empty description="No outpatient surveys"/>}
                                    </XCard>
                                </Col>
                            </Row>
                            {surveyData.surveys.length>1&&(
                                <XCard title="Survey Scores Over Time" filename="Survey_Timeline" style={{marginBottom:16}}>
                                    <ReactECharts option={surveyTimelineOpt()} style={{height:200}}/>
                                </XCard>
                            )}
                            <XCard title="Survey Coverage by Facility" filename="Survey_Coverage" style={{marginBottom:16}}>
                                <Table size="small" dataSource={Object.values(surveyData.by_facility)} rowKey="name" pagination={{pageSize:15,size:'small'}}
                                    columns={[
                                        {title:'Facility',dataIndex:'name',ellipsis:true,width:200},
                                        {title:'IP Surveys',width:90,align:'center',render:(_,r)=><Tag color="blue" style={{fontSize:10}}>{r.ip.length}</Tag>},
                                        {title:'IP Avg Score',width:110,align:'center',render:(_,r)=>{if(!r.ip.length)return<span style={{color:'#ccc'}}>—</span>;const avg=r.ip.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/r.ip.length;return<span style={{fontWeight:700,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'OP Surveys',width:90,align:'center',render:(_,r)=><Tag color="green" style={{fontSize:10}}>{r.op.length}</Tag>},
                                        {title:'OP Avg Score',width:110,align:'center',render:(_,r)=>{if(!r.op.length)return<span style={{color:'#ccc'}}>—</span>;const avg=r.op.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/r.op.length;return<span style={{fontWeight:700,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'Combined Avg',width:120,align:'center',render:(_,r)=>{const all=[...r.ip,...r.op];if(!all.length)return<span style={{color:'#ccc'}}>—</span>;const avg=all.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/all.length;return<span style={{fontWeight:900,fontSize:14,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'Latest Survey',width:110,render:(_,r)=>{const all=[...r.ip,...r.op].sort((a,b)=>moment(b.survey_date).unix()-moment(a.survey_date).unix());return all.length?<span style={{fontSize:11}}>{moment(all[0].survey_date).format('DD MMM YYYY')}</span>:'—';}},
                                    ]}/>
                            </XCard>
                            <XCard title="Recent Surveys" filename="Recent_Surveys" style={{marginTop:16}}>
                                <Table size="small"
                                    dataSource={[...surveyData.surveys].sort((a,b)=>moment(b.survey_date).unix()-moment(a.survey_date).unix()).slice(0,20)}
                                    rowKey="id" pagination={false}
                                    columns={[
                                        {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                        {title:'Type',dataIndex:'survey_type',width:100,render:t=><Tag color={t==='inpatient'?'blue':'green'} style={{textTransform:'capitalize',fontSize:10}}>{t}</Tag>},
                                        {title:'Date',dataIndex:'survey_date',width:110,render:d=>moment(d).format('DD MMM YYYY')},
                                        {title:'Score',dataIndex:'overall_score',width:130,render:s=>{const v=parseFloat(s||0);return<div><span style={{fontWeight:700,fontSize:14,color:surveyScoreColor(v)}}>{v.toFixed(1)}%</span><Progress percent={v} showInfo={false} size="small" strokeColor={surveyScoreColor(v)} style={{marginTop:2}}/></div>;}},
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=>p?<Tag color="blue" style={{fontSize:10}}>{p}</Tag>:<span style={{color:'#ccc'}}>—</span>},
                                    ]}/>
                            </XCard>
                        </>
                    )}


                {/* ════ SECTION 10 — PERIOD COMPARISON ════ */}
                <SectionTitle id="sec-comparison" icon="🔄" title="Period Comparison"
                    subtitle="Compare facility assessment scores across two audit periods side by side"
                    color="#0ea5e9"/>
                <PeriodComparison periods={periods} filters={filters} standards={standards} allClauses={allClauses}/>

                </Spin>
            </div>
        </div>
        {reportFacility && (
            <FacilityReportModal facilityProp={reportFacility} onClose={()=>setReportFacility(null)}/>
        )}
        </>
    );
}